import React, { Component, useState } from 'react';
import PropTypes from 'prop-types';
import { Tabs, Form, Row, Col, Alert, List, Divider, Tooltip, Icon, Switch, Card, Tag, Rate, DatePicker, message } from 'antd';
import { withRouter } from 'react-router-dom';
import moment from 'moment';
import queryString from 'query-string';

import {
  SEPARATOR_DASH,
  STANDARD_RATE_CODE,
  RATE_DISTRIBUTION_TAB,
  RATE_DERIVATIVE,
  RATE_DERIVATIVES,
  AIRBNB_GUEST_REVIEW_CATEGORY,
  AIRBNB_CANCELLATION_POLICIES
} from 'utils/constants';
import { getConstantsNew } from 'utils/apis/constants';
import { getAirbnbAdvanceOptions } from 'utils/apis/integration';
import { getWebRateByRoomType } from 'utils/apis/rate';
import { guard } from 'utils/general';

import FormInput, { FormInputReadOnly } from 'components/FormInput/FormInput';
import FormInputNumber from 'components/FormInputNumber/FormInputNumber';
import FormSelection, { FormMultiSelectionReadOnly } from 'components/FormSelection/FormSelection';
import FormRadioButton from 'components/FormRadioButton/FormRadioButton';
import FormCheckbox from 'components/FormCheckbox/FormCheckbox';
import FormTimePicker from 'components/FormTimePicker/FormTimePicker';

import WrappedTaxProvided from 'components/DynamicFieldSet/TaxFieldSet';
import AdvancedOptionsModal from '../../../../AdvancedOptionsModal/AdvancedOptionsModal';
import AdvancedOptionsRateDistributionTab from '../../../../AdvancedOptionsRateDistributionTab/AdvancedOptionsRateDistributionTab';
import './AirbnbAdvanceOptionModal.css';
import intl from 'react-intl-universal';
import { getCurrencyExchangeRate } from 'utils/apis/currencyExchange';

const TabPane = Tabs.TabPane;

const AIRBNB_RESORT_FEE_KEY = 'PASS_THROUGH_RESORT_FEE';
const AIRBNB_MANAGEMENT_FEE_KEY = 'PASS_THROUGH_MANAGEMENT_FEE';
const AIRBNB_COMMUNITY_FEE_KEY = 'PASS_THROUGH_COMMUNITY_FEE';
const AIRBNB_LINEN_FEE_KEY = 'PASS_THROUGH_LINEN_FEE';
const AIRBNB_CLEANING_FEE = 'PASS_THROUGH_CLEANING_FEE';
const AIRBNB_SHORT_STAY_CLEANING_FEE = 'PASS_THROUGH_SHORT_TERM_CLEANING_FEE';
const AIRBNB_LISTING_PROMOTION_TYPE = {
  HIGH_RATED_GUEST_DISCOUNT: 'HIGH_RATED_GUEST_DISCOUNT',
  MOBILE_ONLY_DISCOUNT: 'MOBILE_ONLY_DISCOUNT'
};
const AIRBNB_LISTING_PROMOTION_BLACKOUT_QUOTA = 30;
const AIRBNB_LISTING_PROMOTION_BLACKOUT_HORIZON_YEARS = 2;

const countBlackoutDatesByYear = dates => {
  return (dates || []).reduce((counts, date) => {
    const year = String(date).slice(0, 4);
    counts[year] = (counts[year] || 0) + 1;
    return counts;
  }, {});
};

const getBlackoutQuotaExceededYear = dates => {
  const counts = countBlackoutDatesByYear(dates);
  return Object.keys(counts).find(year => counts[year] > AIRBNB_LISTING_PROMOTION_BLACKOUT_QUOTA);
};

const LinkToBooking = ({ resId, resCode }) => (
  <a target="_blank" href={`/reservation/${resId}/?selectedTab=reviews`} rel="noopener noreferrer">
    &nbsp; <Icon type="link" /> {`${resCode}`}
  </a>
);

const getRateDistributionPayload = fieldValues => {
  let payload = [];

  const fieldValuesWithKeys = Object.entries(fieldValues);
  const processedFieldValuesWithKeys = fieldValuesWithKeys.filter(fv => fv[0].includes(RATE_DISTRIBUTION_TAB));

  for (let i = 0; i < processedFieldValuesWithKeys.length; i++) {
    const fieldValueWithKey = processedFieldValuesWithKeys[i];
    const fieldKey = fieldValueWithKey[0];
    const fieldValue = fieldValueWithKey[1];

    const splittedFieldKeys = fieldKey.split(SEPARATOR_DASH);
    const roomId = splittedFieldKeys[1];
    const otaId = splittedFieldKeys[2];
    const rateId = splittedFieldKeys[3];
    const fieldName = splittedFieldKeys[4];

    let existingRoom = payload.find(room => room.roomId === roomId);

    if (!existingRoom) {
      existingRoom = { roomId, rates: [] };
      payload.push(existingRoom);
    }

    let existingRateObject = existingRoom.rates.find(rateObject => rateObject.rate._id === rateId || rateObject.otaId === otaId);

    if (!existingRateObject) {
      existingRateObject = { otaId, rate: { _id: rateId } };
      existingRoom.rates.push(existingRateObject);
    }

    if (fieldName === RATE_DERIVATIVE) {
      const derivativeObject = RATE_DERIVATIVES.find(rateDerivative => rateDerivative.code === fieldValue);
      existingRateObject.rate.isDerived = derivativeObject.isDerived;
    } else {
      existingRateObject.rate[fieldName] = fieldValue;
    }
  }
  return payload;
};

const getFilteredSubTypeSelections = (selectedParentValue, selectionsToFilter, parentSelections) => {
  let filteredKeys;
  if (selectedParentValue) {
    filteredKeys = Object.keys(selectionsToFilter).filter(key => parentSelections[selectedParentValue].allowSubTypes.includes(key));
  } else {
    filteredKeys = Object.keys(selectionsToFilter);
  }

  return filteredKeys.map(filteredKey => ({
    key: selectionsToFilter[filteredKey].code,
    value: selectionsToFilter[filteredKey].code,
    displayValue: selectionsToFilter[filteredKey].label
  }));
};

const splitUnderscoreAndCapitalize = unformattedString => {
  return unformattedString
    .split('_')
    .map(splittedKey => splittedKey.charAt(0).toUpperCase() + splittedKey.slice(1))
    .join(' ');
};

const mapConstantsToSelections = constants =>
  Object.values(constants).map(eachConstant => ({
    key: eachConstant.code,
    value: eachConstant.code,
    displayValue: eachConstant.label
  }));

const getRealFieldValue = (form, defaultValues, key) => {
  return form.getFieldValue(key) !== undefined ? form.getFieldValue(key) : defaultValues[key];
};
const DetailsForm = ({ form, defaultValues, propertyTypeGroups, propertyTypes, roomTypes, airbnbCheckinOptions }) => {
  const propertyTypeGroupSelections = mapConstantsToSelections(propertyTypeGroups);
  const airbnbCheckinOptionsSelections = mapConstantsToSelections(airbnbCheckinOptions);
  const propertyTypesSelections = getFilteredSubTypeSelections(form.getFieldValue('propertyTypeGroup'), propertyTypes, propertyTypeGroups);
  const roomTypesSelections = getFilteredSubTypeSelections(form.getFieldValue('propertyTypeCategory'), roomTypes, propertyTypes);
  return (
    <div className="scrollBarStyle">
      <h3>{intl.get('hostConnect.integration.headerLabels.title').d('Title & Description')}</h3>
      <FormInput
        form={form}
        formLabel={intl.get('hostConnect.integration.headerLabels.titleLabel').d('Title')}
        name="title"
        className="fullLengthTextBox"
        defaultValue={defaultValues.title}
        requiredErrorMessage={intl.get('hostConnect.integration.placeholder.selectAirbnbIdMsg').d('This is required')}
        extraRules={[
          {
            min: 8,
            message: intl.get('hostConnect.integration.placeholder.titleMsg').d('Title need to be at least 8 characters')
          }
        ]}
      />
      <FormInputReadOnly
        className="fullLengthTextBox"
        label={intl.get('hostConnect.integration.headerLabels.summary').d('Summary')}
        text={defaultValues.summary}
      />
      <FormInputReadOnly
        className="fullLengthTextBox"
        label={intl.get('hostConnect.integration.headerLabels.space').d('The Space')}
        text={defaultValues.space}
      />
      <FormInputReadOnly
        className="fullLengthTextBox"
        label={intl.get('hostConnect.integration.headerLabels.guestAccess').d('Guest Access')}
        text={defaultValues.access}
      />
      <FormInputReadOnly
        className="fullLengthTextBox"
        label={intl.get('hostConnect.integration.headerLabels.guestInteraction').d('Guest interaction')}
        text={defaultValues.interaction || ''}
      />
      <FormInputReadOnly
        className="fullLengthTextBox"
        label={intl.get('hostConnect.integration.headerLabels.other').d('Other things to note')}
        text={defaultValues.notes || ''}
      />
      <FormInputReadOnly
        className="fullLengthTextBox"
        label={intl.get('hostConnect.integration.headerLabels.neighborhood').d('The neighborhood')}
        text={defaultValues.neighborhood}
      />

      <h3>{intl.get('hostConnect.integration.headerLabels.roomGuest').d('Room & Guests')} </h3>
      <FormInputReadOnly
        className="fullLengthTextBox"
        label={intl.get('hostConnect.integration.headerLabels.bathroom').d('Bathroom')}
        text={defaultValues.numOfBathroom}
      />
      <p>{intl.get('hostConnect.integration.headerLabels.bedroom').d('Bedrooms')} </p>
      <List itemLayout="horizontal" locale={{ emptyText: intl.get('hostConnect.integration.headerLabels.noBedroom').d('No bedroom added') }}>
        {defaultValues.rooms.map((room, index) => {
          const description = room.beds.reduce((roomBedsDescription, bed, i) => {
            const stringBefore = i === 0 ? `${roomBedsDescription}` : `${roomBedsDescription},`;
            return `${stringBefore} ${bed.quantity} ${splitUnderscoreAndCapitalize(bed.type)}`;
          }, '');
          return (
            <List.Item key={index}>
              <List.Item.Meta
                title={`${intl.get('hostConnect.integration.headerLabels.room').d('Room')} ${room.room_number}`}
                description={description}
              />
            </List.Item>
          );
        })}
      </List>
      <FormSelection
        form={form}
        formLabel={intl.get('hostConnect.integration.headerLabels.mostLike').d('Which is most like your place?')}
        name="propertyTypeGroup"
        className="fullLengthTextBox"
        defaultValue={defaultValues.propertyTypeGroup}
        selections={propertyTypeGroupSelections}
        onChange={() => {
          form.setFieldsValue({ propertyTypeCategory: '', roomTypeCategory: '' });
        }}
      />
      {getRealFieldValue(form, defaultValues, 'propertyTypeGroup') && (
        <FormSelection
          form={form}
          formLabel={intl.get('hostConnect.integration.headerLabels.propertyType').d('Property Type')}
          name="propertyTypeCategory"
          className="fullLengthTextBox"
          defaultValue={defaultValues.propertyTypeCategory}
          selections={propertyTypesSelections}
          onChange={() => {
            form.setFieldsValue({ roomTypeCategory: '' });
          }}
        />
      )}
      {getRealFieldValue(form, defaultValues, 'propertyTypeCategory') && (
        <FormSelection
          form={form}
          formLabel={intl.get('hostConnect.integration.headerLabels.listingType').d('Listing Type')}
          name="roomTypeCategory"
          className="fullLengthTextBox"
          defaultValue={defaultValues.roomTypeCategory}
          selections={roomTypesSelections}
        />
      )}

      <h3>{intl.get('hostConnect.integration.headerLabels.amnetities').d('Amenities')}</h3>
      <FormMultiSelectionReadOnly className="fullLengthTextBox" label="Amenities" selections={defaultValues.amenities} size="large" />

      <h3>{intl.get('hostConnect.integration.headerLabels.location').d('Location')}</h3>
      <FormInputReadOnly className="fullLengthTextBox" label={intl.get('host.headerLabels.street').d('Street Address')} text={defaultValues.street} />
      <FormInputReadOnly className="fullLengthTextBox" label={intl.get('host.headerLabels.city').d('City')} text={defaultValues.city} />
      <FormInputReadOnly className="fullLengthTextBox" label={intl.get('host.headerLabels.zip').d('ZIP Code')} text={defaultValues.zipcode} />
      <FormInputReadOnly className="fullLengthTextBox" label={intl.get('host.headerLabels.state').d('State')} text={defaultValues.state} />
      <FormInputReadOnly className="fullLengthTextBox" label={intl.get('host.headerLabels.country').d('Country')} text={defaultValues.country} />

      <h3>{intl.get('hostConnect.integration.headerLabels.guestResource').d('Guest Resource')}</h3>
      <FormSelection
        form={form}
        formLabel={intl.get('hostConnect.integration.headerLabels.checkInMethod').d('Select a Check-in Method')}
        name="checkinOption"
        className="fullLengthTextBox"
        defaultValue={defaultValues.checkinOption}
        selections={airbnbCheckinOptionsSelections}
      />
      {!!getRealFieldValue(form, defaultValues, 'checkinOption') && (
        <FormInput
          form={form}
          formLabel={intl.get('hostConnect.integration.headerLabels.checkInInstruction').d('Check-in Instructions')}
          name="checkinInstruction"
          className="fullLengthTextBox"
          defaultValue={defaultValues.checkinInstruction}
          placeholder={intl.get('hostConnect.integration.placeholder.checkInInstruction').d('Instructions for guest on how to check in.')}
          extraRules={[
            {
              required: !!getRealFieldValue(form, defaultValues, 'checkinOption'),
              message: intl.get('hostConnect.integration.placeholder.selectAirbnbIdMsg').d('This is required')
            }
          ]}
        />
      )}
    </div>
  );
};

const AIRBNB_RATE_TYPE_FLAT_KEY = 'flat';
const AIRBNB_RATE_TYPE_PERCENT_KEY = 'percent';
const AIRBNB_FEE_MULTIPLIER = 1000000;
const calcCorrectFee = (feeType, fee, isDisplay = false) => {
  if (feeType === AIRBNB_RATE_TYPE_FLAT_KEY) {
    if (isDisplay) {
      return fee / AIRBNB_FEE_MULTIPLIER;
    } else {
      return fee * AIRBNB_FEE_MULTIPLIER;
    }
  }
  return fee;
};
const calcCorrectDiscountFactor = (discountPercentage = 0, isDisplay = false) => {
  if (discountPercentage !== undefined && discountPercentage !== null) {
    if (isDisplay) {
      return 100 - Number(discountPercentage) * 100;
    } else {
      return (100 - Number(discountPercentage)) / 100;
    }
  }
  return undefined;
};
const getFeeType = (feeName, form, defaultValues) => {
  return form.getFieldValue(`${feeName}Type`) || defaultValues[feeName].amount_type;
};
const checkFeeTypeIsPercentage = (feeName, form, defaultValues) => {
  return getFeeType(feeName, form, defaultValues) === AIRBNB_RATE_TYPE_PERCENT_KEY;
};
const FeeTypeSelection = ({ form, name, defaultValue, isFlatOnly = false, currency = 'RM' }) => {
  const selections = [
    {
      key: AIRBNB_RATE_TYPE_FLAT_KEY,
      value: AIRBNB_RATE_TYPE_FLAT_KEY,
      displayValue: `${intl.get('hostConnect.integration.headerLabels.flatRate').d('Flat Rate')} (${currency})`
    }
  ];
  if (!isFlatOnly) {
    selections.push({
      key: AIRBNB_RATE_TYPE_PERCENT_KEY,
      value: AIRBNB_RATE_TYPE_PERCENT_KEY,
      displayValue: intl.get('hostConnect.integration.headerLabels.percentage').d('Percentage (%)')
    });
  }
  return (
    <FormSelection
      form={form}
      formLabel={intl.get('hostConnect.integration.headerLabels.feeType').d('Fee Type')}
      name={name}
      defaultValue={defaultValue}
      selections={selections}
      allowClear={false}
    />
  );
};
const PricingForm = ({ form, defaultValues, currency = 'RM', minExtraGuestFee = 24 }) => {
  const validateFee = (minValue, maxValue) => (rule, value, callback) => {
    if (value !== null && value !== undefined && Number(value) > 0) {
      if (value < minValue) {
        callback(
          new Error(
            `${intl.get('hostConnect.integration.placeholder.feeAtLeast').d('Fees must be at least')} ${currency} ${minValue} ${intl
              .get('hostConnect.integration.placeholder.feeAtLeast2')
              .d('but no more than')} ${currency} ${maxValue}`
          )
        );
      }
      if (value > maxValue) {
        callback(
          new Error(
            `${intl.get('hostConnect.integration.placeholder.feeAtLeast').d('Fees must be at least')} ${currency} ${minValue} ${intl
              .get('hostConnect.integration.placeholder.feeAtLeast2')
              .d('but no more than')} ${currency} ${maxValue}`
          )
        );
      }
    }
    callback();
  };
  return (
    <div className="scrollBarStyle">
      <Row>
        <h3>{intl.get('hostConnect.integration.headerLabels.extraFees').d('Extra Fees')}</h3>
        <Row gutter={16}>
          <Col span={12}>
            <FormInputNumber
              form={form}
              formLabel={`${intl.get('hostConnect.integration.headerLabels.forEachGuest').d('For each guest after (Max')} ${
                defaultValues.maxCapacity
              } ${intl.get('hostConnect.integration.headerLabels.forEachGuest2').d('pax')})`}
              name="guestsIncluded"
              defaultValue={defaultValues.guestsIncluded || defaultValues.maxCapacity}
              minValue={1}
              maxValue={defaultValues.maxCapacity}
            />
          </Col>
          <Col span={12} className="halfLengthTextBox">
            <FormInputNumber
              form={form}
              formLabel={`${intl.get('hostConnect.integration.headerLabels.extraGuestFee').d('Extra Guest Fee')} (${currency})`}
              name="extraGuestFee"
              defaultValue={defaultValues.extraGuestFee}
              minValue={0}
              precision={2}
              extraRules={[{ validator: validateFee(minExtraGuestFee, 999999999) }]}
            />
          </Col>
        </Row>
        {/* <Col span={24} className="fullLengthTextBox">
          <FormInputNumber
            form={form}
            formLabel="Cleaning Fee (RM)"
            name="cleaningFee"
            defaultValue={defaultValues.cleaningFee}
            precision={2}
            minValue={0}
            extraRules={[{ validator: validateFee(21, 2501) }]}
          />
        </Col> */}
      </Row>
      <Row>
        <h3>{intl.get('hostConnect.integration.headerLabels.los').d('Length of Stay Discount')}</h3>
        <Row gutter={16}>
          <Col span={12}>
            <FormInputNumber
              form={form}
              formLabel={intl.get('hostConnect.integration.headerLabels.weeklyDiscount').d('Weekly Discount (%)')}
              name="weeklyDiscount"
              defaultValue={defaultValues.weeklyDiscount}
              minValue={0}
              maxValue={99}
              precision={2}
              extraRules={[
                {
                  validator: (rule, value, callback) => {
                    form.validateFields(['monthlyDiscount'], { force: true });
                    callback();
                  }
                }
              ]}
              placeholder={intl.get('hostConnect.integration.placeholder.los').d('Guest will get discount if they stay more than 7 days')}
            />
          </Col>
          <Col span={12} className="halfLengthTextBox">
            <FormInputNumber
              form={form}
              formLabel={intl.get('hostConnect.integration.headerLabels.monthDiscount').d('Monthly Discount (%)')}
              name="monthlyDiscount"
              defaultValue={defaultValues.monthlyDiscount}
              minValue={0}
              maxValue={99}
              precision={2}
              extraRules={[
                {
                  validator: (rule, value, callback) => {
                    const weeklyDiscount = getRealFieldValue(form, defaultValues, 'weeklyDiscount');
                    if (Number(weeklyDiscount) > 0 && Number(value) > 0 && Number(weeklyDiscount) > Number(value)) {
                      return callback(
                        new Error(
                          intl.get('hostConnect.integration.placeholder.weeklyDiscount').d('Monthly discount must be more than weekly discount')
                        )
                      );
                    }
                    return callback();
                  }
                }
              ]}
              placeholder={intl
                .get('hostConnect.integration.placeholder.monthlyDiscount')
                .d('Guest will get discount if they stay more than a month')}
            />
          </Col>
        </Row>
      </Row>
      {/* <Row>
        <h3>Security Deposit</h3>
        <Col span={24} className="fullLengthTextBox">
          <FormInputNumber
            form={form}
            formLabel="Security Deposit (RM)"
            name="securityDeposit"
            defaultValue={defaultValues.securityDeposit}
            precision={2}
            minValue={0}
            extraRules={[{ validator: validateFee(411, 20532) }]}
          />
        </Col>
      </Row> */}
      <Row>
        <h3>{intl.get('hostConnect.integration.headerLabels.otherFee').d('Other Fees')}</h3>
        <Row gutter={16}>
          <Col span={12}>
            <Row gutter={8}>
              <Col span={10}>
                <FeeTypeSelection
                  form={form}
                  name="stdCleaningFeeType"
                  defaultValue={defaultValues.stdCleaningFee.amount_type}
                  isFlatOnly
                  currency={currency}
                />
              </Col>
              <Col span={14}>
                <FormInputNumber
                  form={form}
                  formLabel={intl.get('hostConnect.integration.headerLabels.cleaningFee').d('Cleaning Fee')}
                  name="stdCleaningFee"
                  // defaultValue={defaultValues.stdCleaningFee.amount || defaultValues.cleaningFee}
                  defaultValue={defaultValues.cleaningFee ? defaultValues.cleaningFee : defaultValues.stdCleaningFee.amount}
                  precision={2}
                  minValue={0}
                  maxValue={checkFeeTypeIsPercentage('stdCleaningFee', form, defaultValues) ? 100 : undefined}
                />
              </Col>
            </Row>
          </Col>
          <Col span={12}>
            <Row gutter={8}>
              <Col span={10}>
                <FeeTypeSelection
                  form={form}
                  name="shortStayCleaningFeeType"
                  defaultValue={defaultValues.shortStayCleaningFee.amount_type}
                  isFlatOnly
                  currency={currency}
                />
              </Col>
              <Col span={14}>
                <FormInputNumber
                  form={form}
                  formLabel={
                    <span>
                      {intl.get('hostConnect.integration.headerLabels.shortStay').d('Short-stay Cleaning Fee ')}
                      <Tooltip
                        title={intl
                          .get('hostConnect.integration.headerLabels.shortStayToolTip')
                          .d('Attract guests booking 1 or 2 nights by setting a lower cleaning fee.')}
                      >
                        <Icon type="question-circle" />
                      </Tooltip>
                    </span>
                  }
                  name="shortStayCleaningFee"
                  defaultValue={defaultValues.shortStayCleaningFee.amount}
                  precision={2}
                  minValue={0}
                  maxValue={
                    checkFeeTypeIsPercentage('shortStayCleaningFee', form, defaultValues)
                      ? 100
                      : form.getFieldValue('stdCleaningFee') > 0
                      ? (form.getFieldValue('stdCleaningFee') || defaultValues.stdCleaningFee.amount || defaultValues.cleaningFee) - 1
                      : form.getFieldValue('stdCleaningFee') === 0 || defaultValues.stdCleaningFee.amount === 0 || defaultValues.cleaningFee === 0
                      ? 0
                      : (form.getFieldValue('stdCleaningFee') || defaultValues.stdCleaningFee.amount || defaultValues.cleaningFee) - 1
                  }
                />
              </Col>
            </Row>
          </Col>
        </Row>
        <Row gutter={16}>
          <Col span={12}>
            <Row gutter={8}>
              <Col span={10}>
                <FeeTypeSelection form={form} name="resortFeeType" defaultValue={defaultValues.resortFee.amount_type} currency={currency} />
              </Col>
              <Col span={14}>
                <FormInputNumber
                  form={form}
                  formLabel={intl.get('hostConnect.integration.headerLabels.resortFee').d('Resort Fee')}
                  name="resortFee"
                  defaultValue={defaultValues.resortFee.amount}
                  precision={2}
                  minValue={0}
                  maxValue={checkFeeTypeIsPercentage('resortFee', form, defaultValues) ? 100 : undefined}
                />
              </Col>
            </Row>
          </Col>
          <Col span={12}>
            <Row gutter={8}>
              <Col span={10}>
                <FeeTypeSelection form={form} name="managementFeeType" defaultValue={defaultValues.managementFee.amount_type} currency={currency} />
              </Col>
              <Col span={14} className="quarterLengthTextBox">
                <FormInputNumber
                  form={form}
                  formLabel={intl.get('hostConnect.integration.headerLabels.managementFee').d('Management Fee')}
                  name="managementFee"
                  defaultValue={defaultValues.managementFee.amount}
                  precision={2}
                  minValue={0}
                  maxValue={checkFeeTypeIsPercentage('managementFee', form, defaultValues) ? 100 : undefined}
                />
              </Col>
            </Row>
          </Col>
        </Row>
        <Row gutter={16}>
          <Col span={12}>
            <Row gutter={8}>
              <Col span={10}>
                <FeeTypeSelection form={form} name="communityFeeType" defaultValue={defaultValues.communityFee.amount_type} currency={currency} />
              </Col>
              <Col span={14}>
                <FormInputNumber
                  form={form}
                  formLabel={intl.get('hostConnect.integration.headerLabels.communityFee').d('Community Fee')}
                  name="communityFee"
                  defaultValue={defaultValues.communityFee.amount}
                  precision={2}
                  minValue={0}
                  maxValue={checkFeeTypeIsPercentage('communityFee', form, defaultValues) ? 100 : undefined}
                />
              </Col>
            </Row>
          </Col>
          <Col span={12}>
            <Row gutter={8}>
              <Col span={10}>
                <FeeTypeSelection form={form} name="linenFeeType" defaultValue={defaultValues.linenFee.amount_type} isFlatOnly currency={currency} />
              </Col>
              <Col span={14} className="quarterLengthTextBox">
                <FormInputNumber
                  form={form}
                  formLabel={intl.get('hostConnect.integration.headerLabels.linenFee').d('Linens Fee')}
                  name="linenFee"
                  defaultValue={defaultValues.linenFee.amount}
                  precision={2}
                  minValue={0}
                  maxValue={checkFeeTypeIsPercentage('linenFee', form, defaultValues) ? 100 : undefined}
                />
              </Col>
            </Row>
          </Col>
        </Row>
      </Row>
    </div>
  );
};

const POLICY_FLEXIBLE_VALUE = 'flexible';
const POLICY_MODERATE_VALUE = 'moderate';
const POLICY_STRICT_VALUE = 'strict_14_with_grace_period';

const getCancellationPolicyDescription = selectedPolicy => {
  switch (selectedPolicy) {
    case POLICY_FLEXIBLE_VALUE:
      return intl.get('hostConnect.integration.refundPolicy.refundOneDay').d('Full refund 1 day prior to arrival');
    case POLICY_MODERATE_VALUE:
      return intl.get('hostConnect.integration.refundPolicy.refundFiveDay').d('Full refund 5 days prior to arrival');
    case POLICY_STRICT_VALUE:
      return intl
        .get('hostConnect.integration.refundPolicy.refund48Hr')
        .d(
          'Full refund for cancellations made within 48 hours of booking, if the check-in date is at least 14 days away. 50% refund for cancellations made at least 7 days before check-in. No refunds for cancellations made within 7 days of check-in.'
        );
    default:
      return '';
  }
};

const AvailabilityForm = ({ form, defaultValues, toggle, toggleCustomValue, toggleGapNight, gapNightToggle }) => {
  const daysInWeekSelections = [
    { value: 0, label: intl.get('hostConnect.integration.days.sunday').d('Sunday') },
    { value: 1, label: intl.get('hostConnect.integration.days.monday').d('Monday') },
    { value: 2, label: intl.get('hostConnect.integration.days.tuesday').d('Tuesday') },
    { value: 3, label: intl.get('hostConnect.integration.days.wednesday').d('Wednesday') },
    { value: 4, label: intl.get('hostConnect.integration.days.thursday').d('Thursday') },
    { value: 5, label: intl.get('hostConnect.integration.days.friday').d('Friday') },
    { value: 6, label: intl.get('hostConnect.integration.days.saturday').d('Saturday') }
  ];
  return (
    <div className="scrollBarStyle">
      <h3>{intl.get('hostConnect.integration.headerLabels.reservationPreferences').d('Reservation Preferences')}</h3>
      <FormRadioButton
        form={form}
        formLabel={intl.get('hostConnect.integration.headerLabels.advanceNotice').d('Advance Notice')}
        name="advanceNotice"
        className="advNoticeRadioBtn"
        buttonStyle="solid"
        defaultValue={defaultValues.advanceNoticeInHours}
        onChange={e => {
          toggle(e);
        }}
        selections={[
          { key: '0', value: 0, displayValue: intl.get('hostConnect.integration.reservationPreferences.instant').d('Instant booking') },
          { key: '24', value: 24, displayValue: intl.get('hostConnect.integration.reservationPreferences.oneDay').d('At least 1 day notice') },
          { key: '48', value: 48, displayValue: intl.get('hostConnect.integration.reservationPreferences.twoDay').d('At least 2 days notice') },
          { key: '72', value: 72, displayValue: intl.get('hostConnect.integration.reservationPreferences.threeDay').d('At least 3 days notice') },
          { key: '168', value: 168, displayValue: intl.get('hostConnect.integration.reservationPreferences.sevenDay').d('At least 7 days notice') }
        ]}
      />
      {toggleCustomValue && (
        <>
          <Tooltip
            title={intl
              .get('hostConnect.integration.headerLabels.customBookingTimeTooltip')
              .d(
                'Example: Selecting 22:00 will mean that guest can book up until 10pm on the same day. While the default 00:00 will allow guest to book anytime.'
              )}
          >
            <span style={{ paddingBottom: '10px' }} className="wrap-text">
              {intl.get('hostConnect.integration.headerLabels.customBookingTime').d('Select Same Day Advance Notice. (Cut off time) ')}
              <Icon type="question-circle-o" />
            </span>
          </Tooltip>
          <div className="shortLengthTextBox">
            <FormTimePicker
              form={form}
              fieldName="customAdvanceNotice"
              format="HH:00"
              defaultValue={defaultValues.advanceNoticeInHours < 24 ? (24 - defaultValues.advanceNoticeInHours).toString() + ':00' : '00:00'}
            />
          </div>
        </>
      )}
      <FormSelection
        form={form}
        formLabel={intl.get('hostConnect.integration.headerLabels.preparationTime').d('Preparation Time')}
        name="preparationTime"
        className="fullLengthTextBox"
        defaultValue={defaultValues.preparationTime}
        selections={[
          { key: 0, value: 0, displayValue: intl.get('hostConnect.integration.preparationTime.none').d('None') },
          {
            key: 1,
            value: 1,
            displayValue: intl.get('hostConnect.integration.preparationTime.block1').d('Block 1 night before and after each reservation')
          },
          {
            key: 2,
            value: 2,
            displayValue: intl.get('hostConnect.integration.preparationTime.block2').d('Block 2 night before and after each reservation')
          }
        ]}
      />

      <h3>{intl.get('hostConnect.integration.headerLabels.tripLength').d('Trip Length')}</h3>
      <FormInputNumber
        form={form}
        formLabel={intl.get('hostConnect.integration.headerLabels.minNight').d('Min Night')}
        name="minNight"
        className="fullLengthTextBox"
        defaultValue={defaultValues.minNight}
        minValue={0}
      />
      <FormInputNumber
        form={form}
        formLabel={
          <>
            <Col span={8} style={{ paddingRight: '5px ' }}>
              {intl.get('hostConnect.integration.headerLabels.gapNight').d('Gap Night')}
            </Col>
            <Col span={12}>
              <Switch
                type="primary"
                onChange={e => {
                  gapNightToggle(e);
                }}
                paddingBottom="8px"
                marginBottom="16px"
                defaultChecked={toggleGapNight}
              />
            </Col>
            <Col span={4}>
              <Tooltip
                title={intl
                  .get('hostConnect.integration.headerLabels.gapNightTooltip')
                  .d(
                    "You can overwrite your minimum night rules for leftover gaps between reservation to encourage more bookings. This will set any available inventory's minimum night between bookinngs to this value. However, seasonal minimum night rules will still be prioritised"
                  )}
              >
                <Icon type="question-circle-o" />
              </Tooltip>
            </Col>
          </>
        }
        name="gapNight"
        className="fullLengthTextBox"
        defaultValue={defaultValues.gapNight}
        minValue={1}
        maxValue={form.getFieldValue('minNight') ? (form.getFieldValue('minNight') - 1 === 0 ? 1 : form.getFieldValue('minNight') - 1) : 1}
        disabled={!toggleGapNight}
      />
      <span>{intl.get('hostConnect.integration.headerLabels.customizeMinStay').d('Customize minimum stay requirements by day of week')}</span>
      <Row gutter={16}>
        <Col xs={20} sm={20} md={3} lg={3}>
          <FormInputNumber
            form={form}
            formLabel={intl.get('hostConnect.integration.headerLabels.monOnly').d('Mon only')}
            name="minNightMon"
            defaultValue={defaultValues.minNightMon}
            minValue={form.getFieldValue('minNight')}
          />
        </Col>
        <Col xs={20} sm={20} md={3} lg={3}>
          <FormInputNumber
            form={form}
            formLabel={intl.get('hostConnect.integration.headerLabels.tueOnly').d('Tue only')}
            name="minNightTue"
            defaultValue={defaultValues.minNightTue}
            minValue={form.getFieldValue('minNight')}
          />
        </Col>
        <Col xs={20} sm={20} md={3} lg={3}>
          <FormInputNumber
            form={form}
            formLabel={intl.get('hostConnect.integration.headerLabels.wedOnly').d('Wed only')}
            name="minNightWed"
            defaultValue={defaultValues.minNightWed}
            minValue={form.getFieldValue('minNight')}
          />
        </Col>
        <Col xs={20} sm={20} md={3} lg={3}>
          <FormInputNumber
            form={form}
            formLabel={intl.get('hostConnect.integration.headerLabels.thuOnly').d('Thu only')}
            name="minNightThu"
            defaultValue={defaultValues.minNightThu}
            minValue={form.getFieldValue('minNight')}
          />
        </Col>
        <Col xs={20} sm={20} md={3} lg={3}>
          <FormInputNumber
            form={form}
            formLabel={intl.get('hostConnect.integration.headerLabels.friOnly').d('Fri only')}
            name="minNightFri"
            defaultValue={defaultValues.minNightFri}
            minValue={form.getFieldValue('minNight')}
          />
        </Col>
        <Col xs={20} sm={20} md={3} lg={3}>
          <FormInputNumber
            form={form}
            formLabel={intl.get('hostConnect.integration.headerLabels.satOnly').d('Sat only')}
            name="minNightSat"
            defaultValue={defaultValues.minNightSat}
            minValue={form.getFieldValue('minNight')}
          />
        </Col>
        <Col xs={20} sm={20} md={3} lg={3}>
          <FormInputNumber
            form={form}
            formLabel={intl.get('hostConnect.integration.headerLabels.sunOnly').d('Sun only')}
            name="minNightSun"
            defaultValue={defaultValues.minNightSun}
            minValue={form.getFieldValue('minNight')}
          />
        </Col>
      </Row>
      <FormInputNumber
        form={form}
        formLabel={
          <Tooltip title={intl.get('hostConnect.integration.headerLabels.maxNightTooltip').d('The maximum nights allowed is 1125.')}>
            {intl.get('hostConnect.integration.headerLabels.maxNight').d('Max nights')} <Icon type="question-circle-o" />
          </Tooltip>
        }
        name="maxNight"
        className="fullLengthTextBox"
        defaultValue={defaultValues.maxNight}
        minValue={0}
        maxValue={1125}
      />

      <h3>{intl.get('hostConnect.integration.headerLabels.checkInCheckOutLabel').d('Check-in and Check-out')}</h3>
      <FormCheckbox
        form={form}
        formLabel={intl.get('hostConnect.integration.headerLabels.checkInAllowed').d('Check-in Allowed (Days)')}
        name="checkInDaysAllowed"
        defaultValue={defaultValues.checkInDaysAllowed} // Read default value from API else default to this
        selections={daysInWeekSelections}
      />

      <FormCheckbox
        form={form}
        formLabel={intl.get('hostConnect.integration.headerLabels.checkOutAllowed').d('Check-out Allowed (Days)')}
        name="checkOutDaysAllowed"
        defaultValue={defaultValues.checkOutDaysAllowed} // Read default value from API else default to this
        selections={daysInWeekSelections}
      />
    </div>
  );
};

const OpportunitiesTab = ({ form, defaultValues, onUpdate }) => {
  return (
    <div className="scrollBarStyle">
      <h3>
        <Tooltip
          title={intl
            .get('hostConnect.integration.headerLabels.oppToolTip')
            .d(
              'Opportunities are recommendations based on what we know guests are searching for on Airbnb, and enabling them could potentially improve booking performance. '
            )}
        >
          {intl.get('hostConnect.integration.headerLabels.oppurtunity').d('Opportunities')}
          <Icon type="question-circle-o" />
        </Tooltip>
      </h3>

      {defaultValues.map(opportunity => {
        const id = opportunity.id;
        const title = opportunity.title;
        const description = opportunity.description;
        const inputFields = opportunity.input_fields;
        const type = opportunity.type;
        return (
          <Card
            key={opportunity.id + opportunity.title}
            style={{ marginBottom: 16 }}
            size="small"
            title={
              inputFields.length > 1 ? (
                <Tooltip
                  title={intl
                    .get('hostConnect.integration.headerLabels.oppToolTip2')
                    .d('Please key in all relevant details in you wish to include this opportunity. ')}
                >
                  {`${title} `}
                  <Icon type="question-circle-o" />
                </Tooltip>
              ) : (
                title
              )
            }
            extra={inputFields.length === 0 && <Switch id={id} type="primary" onChange={e => onUpdate(false, type, '', e)} />}
          >
            <List.Item key={opportunity.id}>
              <List.Item.Meta description={description} />
            </List.Item>
            {inputFields.map((inputField, i) => {
              const inputFieldKey = inputField.key;
              const inputFieldLabel = inputField.key_label || '';
              const extraInfo =
                inputFieldLabel.toString() === 'Show discount'
                  ? 'Months Before Arrival'
                  : inputFieldLabel.toString() === 'When should the discount show up?'
                  ? 'Day(s) before check in'
                  : inputFieldLabel.toString() === 'Monthly discount'
                  ? 'Guest will get discount if they stay more than a month'
                  : inputFieldLabel.toString() === 'Weekly discount'
                  ? 'Guest will get discount if they stay more than a week'
                  : ' %';
              const valueConstraint = inputField.value_constraint;

              if (valueConstraint.value_type === 'INTEGER') {
                return (
                  <>
                    <FormInputNumber
                      form={form}
                      name={inputFieldKey}
                      label={`${inputFieldLabel} (${extraInfo})`}
                      className="quarterLengthTextBox"
                      placeholder={extraInfo}
                      // defaultValue={Number(valueConstraint.min_value)}
                      minValue={Number(valueConstraint.min_value)}
                      maxValue={Number(valueConstraint.max_value)}
                      // requiredErrorMessage={i === 1 ? 'This is required' : ''}
                      onChange={e => onUpdate(true, type, inputFieldKey, e, valueConstraint.value_type)}
                    ></FormInputNumber>
                  </>
                );
              } else if (valueConstraint.value_type === 'PERCENTAGE') {
                return (
                  <>
                    <FormInputNumber
                      form={form}
                      name={inputFieldKey}
                      label={`${inputFieldLabel} %`}
                      className="quarterLengthTextBox"
                      placeholder={extraInfo}
                      minValue={Number(valueConstraint.min_value)}
                      maxValue={Number(valueConstraint.max_value)}
                      onChange={e => onUpdate(true, type, inputFieldKey, e, valueConstraint.value_type)}
                    ></FormInputNumber>
                    {/* <Button id={id} type="primary" onClick={() => onUpdate(true, type, inputFieldKey)}>
                        Enable
                      </Button> */}
                  </>
                );
              } else if (valueConstraint.value_type === 'OPTION') {
                const options = valueConstraint.options;
                return (
                  <>
                    <FormRadioButton
                      form={form}
                      name={inputFieldKey}
                      buttonStyle="solid"
                      selections={[
                        { key: '0', value: options[0], displayValue: options[0] },
                        { key: '1', value: options[1], displayValue: options[1] },
                        { key: '2', value: options[2], displayValue: options[2] },
                        { key: '3', value: options[3], displayValue: options[3] }
                      ]}
                      onChange={e => onUpdate(true, type, inputFieldKey, e, valueConstraint.value_type)}
                    />
                  </>
                );
              }
              return <></>;
            })}
          </Card>
        );
      })}
    </div>
  );
};

const CheckoutTasksTab = ({ form, defaultValues, onToggleEnable }) => {
  return (
    <div className="scrollBarStyle">
      <h3>
        <Tooltip
          title={intl
            .get('hostConnect.integration.message.settingCheckoutList')
            .d('Settings for a list of specific tasks for guests to do before they checkout of the listing.')}
        >
          {intl.get('hostConnect.integration.headerLabels.checkoutTasks').d('Check Out Tasks')}
          <Icon type="question-circle-o" />
        </Tooltip>
      </h3>

      {Object.keys(defaultValues).map(task => {
        const code = defaultValues[task].code;
        const title = defaultValues[task].title;
        const label = defaultValues[task].label;
        const details = defaultValues[task].task_detail;
        const present = defaultValues[task].is_present;
        const taskDetailMaxLen = code === 'additional_requests' ? 400 : 140;
        const localizedTitle = intl.get(`hostConnect.integration.checkoutTask.${code}.title`).d(title);
        const localizedLabel = intl.get(`hostConnect.integration.checkoutTask.${code}.label`).d(label);
        return (
          <Card
            key={code}
            style={{ marginBottom: 16 }}
            size="small"
            title={localizedTitle}
            extra={<Switch id={code} type="primary" onChange={e => onToggleEnable(e, task)} checked={present} />}
          >
            <List.Item key={code}>
              <List.Item.Meta description={localizedLabel} />
            </List.Item>
            <FormInput
              form={form}
              name={task}
              placeholder={intl.get('hostConnect.integration.placeholder.checkoutTaskDetail').d('Describe any steps to be followed specifically.')}
              defaultValue={details}
              disabled={!present}
              extraRules={[
                {
                  max: taskDetailMaxLen,
                  message: intl
                    .get('hostConnect.integration.message.characterLimitMax', { max: taskDetailMaxLen })
                    .d(`No more than ${taskDetailMaxLen} characters`)
                }
              ]}
            />
          </Card>
        );
      })}
    </div>
  );
};

const ResIssuesTab = ({ form, defaultValues }) => {
  return (
    <div className="scrollBarStyle">
      <h3>
        <Tooltip
          title={intl
            .get('hostConnect.integration.message.recentTripIssues')
            .d(
              'Recent trip issues allow you to monitor a log of recent trips that have quality issues and take the appropriate actions to improve quality'
            )}
        >
          {intl.get('hostConnect.integration.headerLabels.recentTripIssue').d('Recent Trip Issues')}
          <Icon type="question-circle-o" />
        </Tooltip>
      </h3>

      {defaultValues.map(reservationIssue => {
        const id = reservationIssue.review_issues.review_id;
        const confirmationCode = reservationIssue.confirmation_code;
        const resId = reservationIssue.res_id;
        const overallRating = reservationIssue.review_issues.low_overall_rating;
        // const csViolation = reservationIssue.cs_violations || [];
        const lowCategoryRatings = reservationIssue.review_issues.low_category_ratings || [];
        const negativeCategoryTags = reservationIssue.review_issues.negative_category_tags || [];
        // const cancellation = reservationIssue.review_issues.cancellation || '';
        return (
          <Card
            key={id}
            // style={{ marginBottom: 16 }}
            size="small"
            title={`${overallRating}-star overall rating for reservation code: ${confirmationCode}`}
            extra={
              <div>
                <LinkToBooking resId={resId} resCode={confirmationCode} />
              </div>
            }
          >
            {lowCategoryRatings && negativeCategoryTags && (
              <Row justify="start">
                {lowCategoryRatings.map(lowCategoryRating => {
                  return (
                    <Row type="flex" style={{ color: 'grey', marginBottom: 16 }}>
                      <p className="fullLengthTextBox">
                        {`${lowCategoryRating.category} - `}
                        <Rate value={lowCategoryRating.rating} disabled={true} />
                      </p>
                    </Row>
                  );
                })}
                <Row gutter={8}>
                  {negativeCategoryTags.map(negativeCategoryTag => {
                    const airbnbReviewDetails = AIRBNB_GUEST_REVIEW_CATEGORY.filter(
                      review => review.tag.toUpperCase() === negativeCategoryTag.tag.toUpperCase()
                    );

                    return <Tag color="orange">{airbnbReviewDetails[0].description}</Tag>;
                  })}
                </Row>
              </Row>

              // </List.Item>
            )}
          </Card>
        );
      })}
    </div>
  );
};

const ListingPromotionBlackoutEditor = ({ applyToAllDates, onChangeApplyToAllDates, blackoutDates, onChangeBlackoutDates }) => {
  const [rangeValue, setRangeValue] = useState(undefined);
  const sortedBlackoutDates = [...new Set(blackoutDates || [])].sort();
  const blackoutDateRanges = [];
  for (let i = 0; i < sortedBlackoutDates.length; i++) {
    const start = sortedBlackoutDates[i];
    let end = start;
    while (i + 1 < sortedBlackoutDates.length && moment(sortedBlackoutDates[i + 1]).diff(moment(end), 'days') === 1) {
      i += 1;
      end = sortedBlackoutDates[i];
    }
    blackoutDateRanges.push({ start, end });
  }
  const blackoutCountsByYear = countBlackoutDatesByYear(sortedBlackoutDates);
  const quotaYears = Object.keys(blackoutCountsByYear).sort();
  if (quotaYears.length === 0) {
    quotaYears.push(String(moment().year()));
  }

  return (
    <div className="listingPromotionBlackout">
      <div className="listingPromotionBlackoutRow">
        <span>{intl.get('hostConnect.integration.promotionTab.applyToAllDates').d('Apply to all dates')}</span>
        <Switch checked={applyToAllDates} onChange={onChangeApplyToAllDates} size="small" />
      </div>
      <p className="listingPromotionBlackoutHint">
        {intl.get('hostConnect.integration.promotionTab.blackoutDatesHint').d('Exclude specific dates from this discount without turning it off.')}
      </p>
      {!applyToAllDates && (
        <div>
          <DatePicker.RangePicker
            value={rangeValue}
            onChange={range => {
              setRangeValue(range);
              if (!range || range.length !== 2 || !range[0] || !range[1]) {
                return;
              }
              const nextDates = [...sortedBlackoutDates];
              const today = moment().startOf('day');
              const cursor = range[0].clone().startOf('day');
              const last = range[1].clone().startOf('day');
              while (cursor.isSameOrBefore(last, 'day')) {
                if (!cursor.isBefore(today, 'day')) {
                  const date = cursor.format('YYYY-MM-DD');
                  if (!nextDates.includes(date)) {
                    nextDates.push(date);
                  }
                }
                cursor.add(1, 'day');
              }
              nextDates.sort();
              if (getBlackoutQuotaExceededYear(nextDates)) {
                message.error(
                  intl
                    .get('hostConnect.integration.promotionTab.blackoutQuotaExceeded', {
                      quota: AIRBNB_LISTING_PROMOTION_BLACKOUT_QUOTA
                    })
                    .d(`You can black out at most ${AIRBNB_LISTING_PROMOTION_BLACKOUT_QUOTA} days per calendar year for this discount.`)
                );
                setRangeValue(undefined);
                return;
              }
              onChangeBlackoutDates(nextDates);
              setRangeValue(undefined);
            }}
            disabledDate={current => {
              if (!current) {
                return false;
              }
              const today = moment().startOf('day');
              const horizonEnd = moment()
                .add(AIRBNB_LISTING_PROMOTION_BLACKOUT_HORIZON_YEARS, 'years')
                .endOf('day');
              return current.isBefore(today, 'day') || current.isAfter(horizonEnd, 'day');
            }}
            style={{ width: '100%' }}
          />
          {quotaYears.map(year => (
            <p key={year} className="listingPromotionBlackoutQuota">
              {intl
                .get('hostConnect.integration.promotionTab.blackoutQuota', {
                  used: blackoutCountsByYear[year] || 0,
                  quota: AIRBNB_LISTING_PROMOTION_BLACKOUT_QUOTA,
                  year
                })
                .d(`${blackoutCountsByYear[year] || 0} of ${AIRBNB_LISTING_PROMOTION_BLACKOUT_QUOTA} blackout days used in ${year}`)}
              <Tooltip title={intl.get('hostConnect.integration.promotionTab.blackoutQuotaTooltip').d('Blackout quotas are set by Airbnb')}>
                <Icon type="question-circle-o" className="listingPromotionBlackoutQuotaIcon" />
              </Tooltip>
            </p>
          ))}
          <div className="listingPromotionBlackoutDates">
            {blackoutDateRanges.length === 0 ? (
              <span className="listingPromotionBlackoutHint">
                {intl
                  .get('hostConnect.integration.promotionTab.noBlackoutDatesSelected')
                  .d('No dates excluded. Select a date range to add blackout dates.')}
              </span>
            ) : (
              blackoutDateRanges.map(dateRange => (
                <Tag
                  key={`${dateRange.start}-${dateRange.end}`}
                  closable
                  onClose={e => {
                    e.preventDefault();
                    const datesToRemove = [];
                    const cursor = moment(dateRange.start);
                    const last = moment(dateRange.end);
                    while (cursor.isSameOrBefore(last, 'day')) {
                      datesToRemove.push(cursor.format('YYYY-MM-DD'));
                      cursor.add(1, 'day');
                    }
                    onChangeBlackoutDates(sortedBlackoutDates.filter(date => !datesToRemove.includes(date)));
                  }}
                >
                  {dateRange.start === dateRange.end ? dateRange.start : `${dateRange.start} – ${dateRange.end}`}
                </Tag>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
};

const PromotionTab = ({
  form,
  promotion,
  airbnbPromotions = null,
  onChange,
  highRatedGuestDiscount,
  mobileOnlyDiscount,
  onChangeHighRatedGuestDiscount,
  onChangeMobileOnlyDiscount,
  highRatedGuestApplyToAllDates,
  highRatedGuestBlackoutDates,
  onChangeHighRatedGuestApplyToAllDates,
  onChangeHighRatedGuestBlackoutDates,
  mobileOnlyApplyToAllDates,
  mobileOnlyBlackoutDates,
  onChangeMobileOnlyApplyToAllDates,
  onChangeMobileOnlyBlackoutDates
}) => {
  const bookingCount = airbnbPromotions && airbnbPromotions.resData && Array.isArray(airbnbPromotions.resData) ? airbnbPromotions.resData.length : 0;
  const promotionStatusRaw = airbnbPromotions && airbnbPromotions.status ? airbnbPromotions.status : null;
  const promotionStatusDisplay = promotionStatusRaw
    ? promotionStatusRaw
    : intl.get('hostConnect.integration.promotionTab.statusUnavailable').d('UNAVAILABLE');
  const eligibilityBookingsError = airbnbPromotions && (!airbnbPromotions.resData || airbnbPromotions.resData.length >= 3);

  return (
    <div className="scrollBarStyle">
      <Card
        key={'promotionCard'}
        size="small"
        title={
          <span>
            <p style={{ fontSize: '16px' }}>{intl.get('hostConnect.integration.promotionTab.cardTitle').d('New listing promotion')}</p>
            <p style={{ color: 'gray' }}>
              {intl.get('hostConnect.integration.promotionTab.cardSubtitle').d('Attract early bookings with an automatic 20% discount')}
            </p>
          </span>
        }
        extra={<Switch checked={promotion} onChange={onChange} disabled={bookingCount > 3} />}
      ></Card>
      <h3>
        <Alert
          description={
            <span>
              {intl.get('hostConnect.integration.promotionTab.infoPart1').d('The New Listing promotion applies a')}{' '}
              <b>{intl.get('hostConnect.integration.promotionTab.infoDiscountBold').d('20% discount')}</b>{' '}
              {intl.get('hostConnect.integration.promotionTab.infoPart2').d("to your listing's next")}{' '}
              <b>{intl.get('hostConnect.integration.promotionTab.infoBookingsBold').d('3 bookings.')}</b>{' '}
              {intl
                .get('hostConnect.integration.promotionTab.infoPart3')
                .d('The promotion will automatically expire once the listing has received three bookings.')}{' '}
              <b>{intl.get('hostConnect.integration.promotionTab.noteLabel').d('Note:')}</b>{' '}
              {intl.get('hostConnect.integration.promotionTab.noteBody').d('Promotions take priority over other discounts and do not stack.')}
            </span>
          }
          type="info"
          showIcon
          style={{ marginTop: '12px', marginBottom: '12px' }}
        />
        <Card
          key={'eligibilityCheck'}
          size="small"
          title={intl.get('hostConnect.integration.promotionTab.eligibilityCardTitle').d('ELIGIBILITY REQUIREMENTS')}
        >
          <Alert
            description={intl
              .get('hostConnect.integration.promotionTab.eligibilityBookings', { count: bookingCount })
              .d(`Listing must have fewer than 3 bookings (Current: ${bookingCount})`)}
            type={eligibilityBookingsError ? 'error' : 'success'}
            showIcon
            style={{ marginTop: '12px' }}
          />
          <Alert
            description={intl
              .get('hostConnect.integration.promotionTab.promotionStatusLabel', { status: promotionStatusDisplay })
              .d(`Promotion Status: ${promotionStatusDisplay}`)}
            type={promotionStatusRaw || promotionStatusRaw === 'ONGOING' || promotionStatusRaw === 'AVAILABLE' ? 'success' : 'error'}
            showIcon
            style={{ marginTop: '12px' }}
          />
        </Card>
      </h3>
      <Card
        key={'highRatedGuestDiscountCard'}
        size="small"
        style={{ marginBottom: 16 }}
        title={
          <span>
            <p style={{ fontSize: '16px' }}>
              {intl.get('hostConnect.integration.promotionTab.highRatedGuestDiscountTitle').d('High Rated Guest Discount')}
            </p>
            <p style={{ color: 'gray' }}>
              {intl
                .get('hostConnect.integration.promotionTab.highRatedGuestDiscountSubtitle')
                .d('15% discount for guests rated 4.8★ or higher with at least 3 Airbnb reviews')}
            </p>
          </span>
        }
        extra={<Switch checked={highRatedGuestDiscount} onChange={onChangeHighRatedGuestDiscount} />}
      >
        {highRatedGuestDiscount && (
          <ListingPromotionBlackoutEditor
            applyToAllDates={highRatedGuestApplyToAllDates}
            onChangeApplyToAllDates={onChangeHighRatedGuestApplyToAllDates}
            blackoutDates={highRatedGuestBlackoutDates}
            onChangeBlackoutDates={onChangeHighRatedGuestBlackoutDates}
          />
        )}
      </Card>
      <Card
        key={'mobileOnlyDiscountCard'}
        size="small"
        title={
          <span>
            <p style={{ fontSize: '16px' }}>{intl.get('hostConnect.integration.promotionTab.mobileOnlyDiscountTitle').d('Mobile Only Discount')}</p>
            <p style={{ color: 'gray' }}>
              {intl
                .get('hostConnect.integration.promotionTab.mobileOnlyDiscountSubtitle')
                .d('10% discount for guests booking on the Airbnb mobile app')}
            </p>
          </span>
        }
        extra={<Switch checked={mobileOnlyDiscount} onChange={onChangeMobileOnlyDiscount} />}
      >
        {mobileOnlyDiscount && (
          <ListingPromotionBlackoutEditor
            applyToAllDates={mobileOnlyApplyToAllDates}
            onChangeApplyToAllDates={onChangeMobileOnlyApplyToAllDates}
            blackoutDates={mobileOnlyBlackoutDates}
            onChangeBlackoutDates={onChangeMobileOnlyBlackoutDates}
          />
        )}
      </Card>
    </div>
  );
};

const TaxForm = ({ form, defaultValues, onChange, currency }) => {
  return (
    <div className="scrollBarStyle">
      <h3>{intl.get('hostConnect.integration.headerLabels.addTax').d('Add a tax')}</h3>
      <WrappedTaxProvided form={form} defaultValues={defaultValues} onChange={onChange} currency={currency} />
    </div>
  );
};

const checkIsReasonRequired = (form, defaultValues) => {
  const formIsSuitableForChildren = form.getFieldValue('isSuitableForChildren');
  const formIsSuitableForInfant = form.getFieldValue('isSuitableForInfant');
  const isSuitableForChildren = formIsSuitableForChildren !== undefined ? formIsSuitableForChildren : defaultValues.isSuitableForChildren;
  const isSuitableForInfant = formIsSuitableForInfant !== undefined ? formIsSuitableForInfant : defaultValues.isSuitableForInfant;
  if (!isSuitableForChildren || !isSuitableForInfant) {
    return true;
  } else {
    return false;
  }
};

const checkMustClimbStairs = (form, defaultValues) => {
  const formValue = form.getFieldValue('hasStairs');
  const isMustClimbStairs = formValue !== undefined ? formValue : defaultValues.houseRules.requires_stairs;
  if (isMustClimbStairs) {
    return true;
  } else {
    return false;
  }
};

const checkPotentialForNoise = (form, defaultValues) => {
  const formValue = form.getFieldValue('hasNoise');
  const isPotentialForNoise = formValue !== undefined ? formValue : defaultValues.houseRules.potential_noise;
  if (isPotentialForNoise) {
    return true;
  } else {
    return false;
  }
};

const checkPetsLiveOnProperty = (form, defaultValues) => {
  const formValue = form.getFieldValue('hasPets');
  const hasPets = formValue !== undefined ? formValue : defaultValues.houseRules.has_pets;
  if (hasPets) {
    return true;
  } else {
    return false;
  }
};

const checkNoParkingOnProperty = (form, defaultValues) => {
  const formValue = form.getFieldValue('hasLimitedParking');
  const hasLimitedParking = formValue !== undefined ? formValue : defaultValues.houseRules.limited_parking;
  if (hasLimitedParking) {
    return true;
  } else {
    return false;
  }
};

const checkSharedSpaces = (form, defaultValues) => {
  const formValue = form.getFieldValue('hasSharedSpaces');
  const hasSharedSpaces = formValue !== undefined ? formValue : defaultValues.houseRules.limited_amenities;
  if (hasSharedSpaces) {
    return true;
  } else {
    return false;
  }
};

const checkAmenityLimitations = (form, defaultValues) => {
  const formValue = form.getFieldValue('hasLimitedAmenities');
  const hasLimitedAmenities = formValue !== undefined ? formValue : defaultValues.houseRules.limited_amenities;
  if (hasLimitedAmenities) {
    return true;
  } else {
    return false;
  }
};

const checkSurveillance = (form, defaultValues) => {
  const formValue = form.getFieldValue('hasSurveillance');
  const hasSurveillance = formValue !== undefined ? formValue : defaultValues.houseRules.surveillance;
  if (hasSurveillance) {
    return true;
  } else {
    return false;
  }
};

const checkWeapons = (form, defaultValues) => {
  const formValue = form.getFieldValue('hasWeapons');
  const hasWeapons = formValue !== undefined ? formValue : defaultValues.houseRules.weapons;
  if (hasWeapons) {
    return true;
  } else {
    return false;
  }
};

const checkDangerousAnimals = (form, defaultValues) => {
  const formValue = form.getFieldValue('hasAnimals');
  const hasAnimals = formValue !== undefined ? formValue : defaultValues.houseRules.animals;
  if (hasAnimals) {
    return true;
  } else {
    return false;
  }
};

const getCancellationPolicyValue = (form, defaultValues) => {
  return form.getFieldValue('cancellationPolicy') || defaultValues.cancellationPolicy;
};

const BookingSettingForm = ({ form, defaultValues }) => {
  const switchGeneralSelections = [
    { key: true, value: true, displayValue: intl.get('booking_form.guestDetails.alertMessage.yes').d('Yes') },
    { key: false, value: false, displayValue: intl.get('booking_form.guestDetails.alertMessage.no').d('No') }
  ];

  return (
    <div className="scrollBarStyle">
      <Row gutter={8}>
        <h3>{intl.get('hostConnect.integration.bookingSetting.houseRule').d('House Rules')}</h3>
        <Col span={20}>
          <p className="toggleTitle">{intl.get('hostConnect.integration.bookingSetting.suitableChild').d('Suitable for Children (2-12 years)')}</p>
        </Col>
        <Col span={4}>
          <FormRadioButton
            form={form}
            name="isSuitableForChildren"
            defaultValue={!!defaultValues.isSuitableForChildren}
            buttonStyle="solid"
            selections={switchGeneralSelections}
          />
        </Col>
        <Col span={20}>
          <p className="toggleTitle">{intl.get('hostConnect.integration.bookingSetting.suitableInfant').d('Suitable for Infants (0-2 years)')}</p>
        </Col>
        <Col span={4}>
          <FormRadioButton
            form={form}
            name="isSuitableForInfant"
            defaultValue={!!defaultValues.isSuitableForInfant}
            buttonStyle="solid"
            selections={switchGeneralSelections}
          />
        </Col>
        {checkIsReasonRequired(form, defaultValues) && (
          <Col span={24} className="fullLengthTextBox">
            <FormInput
              form={form}
              placeholder={intl
                .get('hostConnect.integration.bookingSetting.placeholder.infant')
                .d('Reason why children or infants are not allowed to stay')}
              name="reasonWhyNotAllowChild"
              defaultValue={defaultValues.reasonWhyNotAllowChild}
              extraRules={[
                {
                  required: true,
                  message: intl.get('hostConnect.integration.bookingSetting.placeholder.infantMsg').d('Please provide a reason why.')
                }
              ]}
            />
          </Col>
        )}
        <Divider className="fullLengthTextBox" />
      </Row>
      <Row gutter={8}>
        <Col span={20}>
          <p className="toggleTitle">{intl.get('hostConnect.integration.bookingSetting.pets').d('Pets Allowed')}</p>
        </Col>
        <Col span={4}>
          <FormRadioButton
            form={form}
            name="isPetsAllowed"
            defaultValue={!!defaultValues.isPetsAllowed}
            buttonStyle="solid"
            selections={switchGeneralSelections}
          />
        </Col>
        <Divider className="fullLengthTextBox" />
      </Row>
      <Row gutter={8}>
        <Col span={20}>
          <p className="toggleTitle">{intl.get('hostConnect.integration.bookingSetting.smoking').d('Smoking Allowed')}</p>
        </Col>
        <Col span={4}>
          <FormRadioButton
            form={form}
            name="isSmokingAllowed"
            defaultValue={!!defaultValues.isSmokingAllowed}
            buttonStyle="solid"
            selections={switchGeneralSelections}
          />
        </Col>
        <Divider className="fullLengthTextBox" />
      </Row>
      <Row gutter={8}>
        <Col span={20}>
          <p className="toggleTitle">{intl.get('hostConnect.integration.bookingSetting.party').d('Parties and Events Allowed')}</p>
        </Col>
        <Col span={4}>
          <FormRadioButton
            form={form}
            name="isPartiesAllowed"
            defaultValue={!!defaultValues.isPartiesAllowed}
            buttonStyle="solid"
            selections={switchGeneralSelections}
          />
        </Col>
        <Divider className="fullLengthTextBox" />
      </Row>
      <Row gutter={8}>
        <Col span={20}>
          <p className="toggleTitle">{intl.get('hostConnect.integration.bookingSetting.climbStair').d('Must Climb Stairs')}</p>
        </Col>
        <Col span={4}>
          <FormRadioButton
            form={form}
            name="hasStairs"
            defaultValue={!!defaultValues.houseRules.requires_stairs}
            buttonStyle="solid"
            selections={switchGeneralSelections}
          />
        </Col>
        {checkMustClimbStairs(form, defaultValues) && (
          <Col span={24} className="fullLengthTextBox">
            <FormInput
              form={form}
              placeholder={intl
                .get('hostConnect.integration.bookingSetting.placeholder.climbStair')
                .d('Describe the stairs (for example, how many flights?)')}
              name="stairsDetails"
              defaultValue={defaultValues.houseRules.requires_stairs}
              requiredErrorMessage={intl.get('hostConnect.integration.bookingSetting.placeholder.climbStairMsg').d('Please provide a description.')}
            />
          </Col>
        )}
        <Divider className="fullLengthTextBox" />
      </Row>
      <Row gutter={8}>
        <Col span={20}>
          <p className="toggleTitle">{intl.get('hostConnect.integration.bookingSetting.noise').d('Potential for Noise')}</p>
        </Col>
        <Col span={4}>
          <FormRadioButton
            form={form}
            name="hasNoise"
            defaultValue={!!defaultValues.houseRules.potential_noise}
            buttonStyle="solid"
            selections={switchGeneralSelections}
          />
        </Col>
        {checkPotentialForNoise(form, defaultValues) && (
          <Col span={24} className="fullLengthTextBox">
            <FormInput
              form={form}
              placeholder={intl
                .get('hostConnect.integration.bookingSetting.placeholder.noise')
                .d("Describe the noise and when it's likely to take place")}
              name="noiseDetails"
              defaultValue={defaultValues.houseRules.potential_noise}
              requiredErrorMessage={intl.get('hostConnect.integration.bookingSetting.placeholder.climbStairMsg').d('Please provide a description.')}
            />
          </Col>
        )}
        <Divider className="fullLengthTextBox" />
      </Row>
      <Row gutter={8}>
        <Col span={20}>
          <p className="toggleTitle">{intl.get('hostConnect.integration.bookingSetting.petProperty').d('Pet(s) Live on Property')}</p>
        </Col>
        <Col span={4}>
          <FormRadioButton
            form={form}
            name="hasPets"
            defaultValue={!!defaultValues.houseRules.has_pets}
            buttonStyle="solid"
            selections={switchGeneralSelections}
          />
        </Col>
        {checkPetsLiveOnProperty(form, defaultValues) && (
          <Col span={24} className="fullLengthTextBox">
            <FormInput
              form={form}
              placeholder={intl.get('hostConnect.integration.bookingSetting.placeholder.petProperty').d('Describe the pets you have')}
              name="petsDetails"
              defaultValue={defaultValues.houseRules.has_pets}
              requiredErrorMessage={intl.get('hostConnect.integration.bookingSetting.placeholder.climbStairMsg').d('Please provide a description.')}
            />
          </Col>
        )}
        <Divider className="fullLengthTextBox" />
      </Row>
      <Row gutter={8}>
        <Col span={20}>
          <p className="toggleTitle">{intl.get('hostConnect.integration.bookingSetting.noParking').d('No Parking on Property')}</p>
        </Col>
        <Col span={4}>
          <FormRadioButton
            form={form}
            name="hasLimitedParking"
            defaultValue={!!defaultValues.houseRules.limited_parking}
            buttonStyle="solid"
            selections={switchGeneralSelections}
          />
        </Col>
        {checkNoParkingOnProperty(form, defaultValues) && (
          <Col span={24} className="fullLengthTextBox">
            <FormInput
              form={form}
              placeholder={intl
                .get('hostConnect.integration.bookingSetting.placeholder.noParking')
                .d('Describe the parking situation around your listing')}
              name="limitedParkingDetails"
              defaultValue={defaultValues.houseRules.limited_parking}
              requiredErrorMessage={intl.get('hostConnect.integration.bookingSetting.placeholder.climbStairMsg').d('Please provide a description.')}
            />
          </Col>
        )}
        <Divider className="fullLengthTextBox" />
      </Row>
      <Row gutter={8}>
        <Col span={20}>
          <p className="toggleTitle">{intl.get('hostConnect.integration.bookingSetting.spacesShared').d('Some Spaces are Shared')}</p>
        </Col>
        <Col span={4}>
          <FormRadioButton
            form={form}
            name="hasSharedSpaces"
            defaultValue={!!defaultValues.houseRules.shared_spaces}
            buttonStyle="solid"
            selections={switchGeneralSelections}
          />
        </Col>
        {checkSharedSpaces(form, defaultValues) && (
          <Col span={24} className="fullLengthTextBox">
            <FormInput
              form={form}
              placeholder={intl.get('hostConnect.integration.bookingSetting.placeholder.spacesShared').d('Describe the spaces the guests will share')}
              name="sharedSpacesDetails"
              defaultValue={defaultValues.houseRules.shared_spaces}
              requiredErrorMessage={intl.get('hostConnect.integration.bookingSetting.placeholder.climbStairMsg').d('Please provide a description.')}
            />
          </Col>
        )}
        <Divider className="fullLengthTextBox" />
      </Row>
      <Row gutter={8}>
        <Col span={20}>
          <p className="toggleTitle">{intl.get('hostConnect.integration.bookingSetting.amnetity').d('Amenity Limitations')}</p>
        </Col>
        <Col span={4}>
          <FormRadioButton
            form={form}
            name="hasLimitedAmenities"
            defaultValue={!!defaultValues.houseRules.limited_amenities}
            buttonStyle="solid"
            selections={switchGeneralSelections}
          />
        </Col>
        {checkAmenityLimitations(form, defaultValues) && (
          <Col span={24} className="fullLengthTextBox">
            <FormInput
              form={form}
              placeholder={intl
                .get('hostConnect.integration.bookingSetting.placeholder.amnetity')
                .d("Describe an amenity or service that's limited, such as weak wifi or limited hot water")}
              name="limitedAmenitiesDetails"
              defaultValue={defaultValues.houseRules.limited_amenities}
              requiredErrorMessage={intl.get('hostConnect.integration.bookingSetting.placeholder.climbStairMsg').d('Please provide a description.')}
            />
          </Col>
        )}
        <Divider className="fullLengthTextBox" />
      </Row>
      <Row gutter={8}>
        <Col span={20}>
          <p className="toggleTitle">
            {intl.get('hostConnect.integration.bookingSetting.surveillance').d('Surveillance or Recording Devices on Property')}
          </p>
        </Col>
        <Col span={4}>
          <FormRadioButton
            form={form}
            name="hasSurveillance"
            defaultValue={!!defaultValues.houseRules.surveillance}
            buttonStyle="solid"
            selections={switchGeneralSelections}
          />
        </Col>
        {checkSurveillance(form, defaultValues) && (
          <Col span={24} className="fullLengthTextBox">
            <FormInput
              form={form}
              placeholder={intl
                .get('hostConnect.integration.bookingSetting.placeholder.surveillance')
                .d(
                  "Describe any device that records or sends video, audio, or still images. Specify each device's location and whether it will be on or off."
                )}
              name="surveillanceDetails"
              defaultValue={defaultValues.houseRules.surveillance}
              requiredErrorMessage={intl.get('hostConnect.integration.bookingSetting.placeholder.climbStairMsg').d('Please provide a description.')}
            />
          </Col>
        )}
        <Divider className="fullLengthTextBox" />
      </Row>
      <Row gutter={8}>
        <Col span={20}>
          <p className="toggleTitle">{intl.get('hostConnect.integration.bookingSetting.weapons').d('Weapons on Property')}</p>
        </Col>
        <Col span={4}>
          <FormRadioButton
            form={form}
            name="hasWeapons"
            defaultValue={!!defaultValues.houseRules.weapons}
            buttonStyle="solid"
            selections={switchGeneralSelections}
          />
        </Col>
        {checkWeapons(form, defaultValues) && (
          <Col span={24} className="fullLengthTextBox">
            <FormInput
              form={form}
              placeholder={intl
                .get('hostConnect.integration.bookingSetting.placeholder.weapon')
                .d("Describe any weapons (firearms, airguns, tasers, etc.), their location, and how they're secured")}
              name="weaponsDetails"
              defaultValue={defaultValues.houseRules.weapons}
              requiredErrorMessage={intl.get('hostConnect.integration.bookingSetting.placeholder.climbStairMsg').d('Please provide a description.')}
            />
          </Col>
        )}
        <Divider className="fullLengthTextBox" />
      </Row>
      <Row gutter={8}>
        <Col span={20}>
          <p className="toggleTitle">{intl.get('hostConnect.integration.bookingSetting.dangerous').d('Dangerous Animals on Property')}</p>
        </Col>
        <Col span={4}>
          <FormRadioButton
            form={form}
            name="hasAnimals"
            defaultValue={!!defaultValues.houseRules.animals}
            buttonStyle="solid"
            selections={switchGeneralSelections}
          />
        </Col>
        {checkDangerousAnimals(form, defaultValues) && (
          <Col span={24} className="fullLengthTextBox">
            <FormInput
              form={form}
              placeholder={intl
                .get('hostConnect.integration.bookingSetting.placeholder.dangerous')
                .d('Describe any domesticated or wild animals that could be a health or safety risk to guests or other animals')}
              name="animalsDetails"
              defaultValue={defaultValues.houseRules.animals}
              requiredErrorMessage={intl.get('hostConnect.integration.bookingSetting.placeholder.climbStairMsg').d('Please provide a description.')}
            />
          </Col>
        )}
        <Divider className="fullLengthTextBox" />
      </Row>
      <Row gutter={16}>
        <h3>{intl.get('hostConnect.integration.bookingSetting.policies').d('Policies')}</h3>
        <Col span={12}>
          <FormTimePicker
            form={form}
            label={intl.get('hostConnect.integration.bookingSetting.checkInStart').d('Check-in Start Time')}
            fieldName="checkInStartTime"
            format="HH:00"
            disabledHours={[0, 1, 2, 3, 4, 5, 6, 7]}
            defaultValue={defaultValues.checkInStartTime}
            extraRules={[
              {
                validator: (rule, value, callback) => {
                  form.validateFields(['checkInEndTime'], { force: true });
                  callback();
                }
              }
            ]}
          />
        </Col>
        <Col span={12} className="halfLengthTextBox">
          <FormTimePicker
            form={form}
            label={intl.get('hostConnect.integration.bookingSetting.checkIn').d('Check-in End Time')}
            fieldName="checkInEndTime"
            format="HH:00"
            disabledHours={[0, 1, 2, 3, 4, 5, 6, 7, 8]}
            defaultValue={defaultValues.checkInEndTime}
            extraRules={[
              {
                validator: (rule, value, callback) => {
                  const startDate = getRealFieldValue(form, defaultValues, 'checkInStartTime');
                  if (value && !startDate) {
                    callback(intl.get('hostConnect.integration.bookingSetting.placeholder.startTime').d('Start time must be set'));
                  } else if (!value) {
                    callback();
                  } else {
                    const diffTime = Number(value.format('H')) - Number(startDate.format('H'));
                    if (diffTime < 2) {
                      return callback(
                        new Error(
                          intl.get('hostConnect.integration.bookingSetting.placeholder.endTime').d('End time must be 2 hours ahead than start time')
                        )
                      );
                    }
                    return callback();
                  }
                }
              }
            ]}
          />
        </Col>
      </Row>
      <Row gutter={16}>
        <Col span={12}>
          <FormTimePicker
            form={form}
            label={intl.get('hostConnect.integration.bookingSetting.checkOut').d('Check-out Time')}
            fieldName="checkOutTime"
            format="HH:00"
            defaultValue={defaultValues.checkOutTime ? String(defaultValues.checkOutTime) : defaultValues.checkOutTime}
          />
        </Col>
        <Col span={12}>
          <FormRadioButton
            form={form}
            label={intl.get('hostConnect.integration.bookingSetting.cancellation').d('Cancellation Policy')}
            name="cancellationPolicy"
            buttonStyle="solid"
            defaultValue={defaultValues.cancellationPolicy}
            selections={[
              {
                key: POLICY_FLEXIBLE_VALUE,
                value: POLICY_FLEXIBLE_VALUE,
                displayValue: intl.get('hostConnect.integration.cancelPolicy.flexible').d('Flexible')
              },
              {
                key: POLICY_MODERATE_VALUE,
                value: POLICY_MODERATE_VALUE,
                displayValue: intl.get('hostConnect.integration.cancelPolicy.moderate').d('Moderate')
              },
              {
                key: POLICY_STRICT_VALUE,
                value: POLICY_STRICT_VALUE,
                displayValue: intl.get('hostConnect.integration.cancelPolicy.strict').d('Strict')
              }
            ]}
          />
        </Col>
        <Col span={24} className="fullLengthTextBox">
          {getCancellationPolicyValue(form, defaultValues) && (
            <Alert
              message={intl.get('hostConnect.integration.bookingSetting.tips').d('Tips')}
              description={getCancellationPolicyDescription(getCancellationPolicyValue(form, defaultValues))}
              type="info"
              showIcon
              style={{ marginBottom: '16px' }}
            />
          )}
        </Col>
      </Row>
    </div>
  );
};

const defaultStandardFee = { amount: 0, amount_type: 'flat' };

class AirbnbAdvanceOptionModal extends Component {
  constructor(props) {
    super(props);
    this.state = {
      isLoading: false,
      toggleCustomValue: false,
      roomsOfRates: [],
      propertyTypeGroups: {},
      propertyTypes: {},
      roomTypes: {},
      calculatedMinExtraGuestFee: 24,
      airbnbCheckinOptions: {},
      airbnbCheckoutTasks: {},
      defaultDescription: {
        title: '',
        summary: '',
        space: '',
        access: '',
        interaction: '',
        notes: '',
        neighborhood: ''
      },
      defaultRoom: {
        numOfBathroom: 0,
        rooms: [],
        propertyTypeGroup: '',
        propertyTypeCategory: '',
        roomTypeCategory: ''
      },
      defaultAmenities: {
        amenities: []
      },
      defaultAddress: {
        street: '',
        city: '',
        zipcode: '',
        state: '',
        country: ''
      },
      defaultGuestResource: {
        checkinOption: '',
        checkinInstruction: ''
      },
      defaultPricing: {
        guestsIncluded: undefined,
        extraGuestFee: 0,
        cleaningFee: 0,
        weeklyDiscount: 0,
        monthlyDiscount: 0,
        // securityDeposit: 0,
        resortFee: defaultStandardFee,
        managementFee: defaultStandardFee,
        communityFee: defaultStandardFee,
        linenFee: defaultStandardFee,
        stdCleaningFee: defaultStandardFee,
        shortStayCleaningFee: defaultStandardFee,
        taxes: []
      },
      defaultAvailability: {
        advanceNoticeInHours: undefined,
        preparationTime: 0,
        minNight: 1,
        minNightMon: 1,
        minNightTue: 1,
        minNightWed: 1,
        minNightThu: 1,
        minNightFri: 1,
        minNightSat: 1,
        maxNightSun: 1,
        checkInDaysAllowed: [],
        checkOutDaysAllowed: []
      },
      defaultBookingSetting: {
        isSuitableForChildren: false,
        isSuitableForInfant: false,
        isPartiesAllowed: false,
        isPetsAllowed: false,
        isSmokingAllowed: false,
        reasonWhyNotAllowChild: '',
        checkInStartTime: undefined,
        checkInEndTime: undefined,
        checkOutTime: undefined,
        cancellationPolicy: undefined,
        houseRules: []
      },
      opportunities: [],
      opportunitiesToUpdate: [],
      reservationIssues: [],
      promotion: false,
      highRatedGuestDiscount: false,
      highRatedGuestApplyToAllDates: true,
      highRatedGuestBlackoutDates: [],
      mobileOnlyDiscount: false,
      mobileOnlyApplyToAllDates: true,
      mobileOnlyBlackoutDates: [],
      airbnbPromotions: [],
      toggleGapNight: false
    };

    this.handleOnSave = this.handleOnSave.bind(this);
    this.handleOnUpdate = this.handleOnUpdate.bind(this);
    this.handleToggleEnable = this.handleToggleEnable.bind(this);
    this.handleEnablePromotion = this.handleEnablePromotion.bind(this);
    this.handleEnableHighRatedGuestDiscount = this.handleEnableHighRatedGuestDiscount.bind(this);
    this.handleEnableMobileOnlyDiscount = this.handleEnableMobileOnlyDiscount.bind(this);
    this.handleGapNightToggle = this.handleGapNightToggle.bind(this);
  }

  async componentDidMount() {
    const { unitId, airbnbListingId, defaultValues, isFullSync, rateModifierTypeConstants, rateModifierSignConstants, currency } = this.props;

    this.setState({
      isLoading: true
    });
    this.constuctDefaultMinExtraGuestFee(currency);
    const [{ data }, propertyTypeGroups, propertyTypes, roomTypes, airbnbCheckinOptions, airbnbCheckoutTasks, roomsOfRates] = await Promise.all([
      getAirbnbAdvanceOptions(unitId, airbnbListingId),
      getConstantsNew('airbnbPropertyTypeGroups'),
      getConstantsNew('airbnbPropertyTypes'),
      getConstantsNew('airbnbRoomTypes'),
      getConstantsNew('airbnbCheckinOptions'),
      getConstantsNew('airbnbCheckoutTasks'),
      this.constructRoomsOfRates({ rateModifierTypeConstants, rateModifierSignConstants })
    ]);

    const mergedDefaultValues = this.mergeDefaultValuesWithData(data, defaultValues);
    let newStateObj = {
      isLoading: false,
      propertyTypeGroups,
      propertyTypes,
      roomTypes,
      airbnbCheckinOptions,
      airbnbCheckoutTasks: this.constructDefaultCheckoutTasks(mergedDefaultValues, airbnbCheckoutTasks),
      roomsOfRates,
      defaultPricing: this.constructDefaultPricing(mergedDefaultValues),
      defaultAvailability: this.constructDefaultAvailability(mergedDefaultValues),
      defaultBookingSetting: this.constructDefaultBookingSetting(mergedDefaultValues),
      opportunities: mergedDefaultValues.opportunities || [],
      reservationIssues: mergedDefaultValues.listing.reservation_issues || [],
      airbnbPromotions: mergedDefaultValues.airbnb_promotions || []
    };
    if (isFullSync) {
      newStateObj.defaultRoom = this.constructDefaultRoom(mergedDefaultValues);
      newStateObj.defaultDescription = this.constructDefaultDescription(mergedDefaultValues);
      newStateObj.defaultAmenities = this.constructDefaultAmenities(mergedDefaultValues);
      newStateObj.defaultAddress = this.constructDefaultAddress(mergedDefaultValues);
      newStateObj.defaultGuestResource = this.constructDefaultGuestResource(mergedDefaultValues);
      // newStateObj.defaultBookingSetting = this.constructDefaultBookingSetting(mergedDefaultValues);
    }
    if (newStateObj.defaultAvailability.advanceNoticeInHours < 24) {
      newStateObj.toggleCustomValue = true;
    }
    if (newStateObj.defaultAvailability.enable_gap_nights) {
      newStateObj.toggleGapNight = true;
    }
    if (newStateObj.airbnbPromotions.status && newStateObj.airbnbPromotions.status === 'ONGOING') {
      newStateObj.promotion = true;
    }
    const listingPromotions = Array.isArray(mergedDefaultValues.listing_promotions) ? mergedDefaultValues.listing_promotions : [];
    const highRatedGuestPromotion =
      listingPromotions.find(promo => promo && promo.type === AIRBNB_LISTING_PROMOTION_TYPE.HIGH_RATED_GUEST_DISCOUNT) || {};
    const mobileOnlyPromotion = listingPromotions.find(promo => promo && promo.type === AIRBNB_LISTING_PROMOTION_TYPE.MOBILE_ONLY_DISCOUNT) || {};
    const highRatedGuestBlackoutDates = Array.isArray(highRatedGuestPromotion.blackout_dates) ? highRatedGuestPromotion.blackout_dates : [];
    const mobileOnlyBlackoutDates = Array.isArray(mobileOnlyPromotion.blackout_dates) ? mobileOnlyPromotion.blackout_dates : [];
    newStateObj.highRatedGuestDiscount = !!highRatedGuestPromotion.enabled;
    newStateObj.highRatedGuestBlackoutDates = highRatedGuestBlackoutDates;
    newStateObj.highRatedGuestApplyToAllDates = highRatedGuestBlackoutDates.length === 0;
    newStateObj.mobileOnlyDiscount = !!mobileOnlyPromotion.enabled;
    newStateObj.mobileOnlyBlackoutDates = mobileOnlyBlackoutDates;
    newStateObj.mobileOnlyApplyToAllDates = mobileOnlyBlackoutDates.length === 0;

    this.setState(newStateObj);
  }

  mergeDefaultValuesWithData(data, defaultValues) {
    if (!defaultValues || Object.keys(defaultValues).length === 0) {
      return data;
    }
    return Object.keys(defaultValues).reduce((mergedValues, key) => {
      const currentData = data[key];
      if (currentData) {
        const removedFalsyValueCurrentData = Object.keys(currentData).reduce((newCurrentValue, currentValueKey) => {
          if (currentData[currentValueKey] !== undefined) {
            return {
              ...newCurrentValue,
              [currentValueKey]: currentData[currentValueKey]
            };
          }
          return newCurrentValue;
        }, {});
        return {
          ...mergedValues,
          [key]: {
            ...removedFalsyValueCurrentData,
            ...defaultValues[key]
          }
        };
      } else {
        return {
          ...mergedValues,
          [key]: defaultValues[key]
        };
      }
    }, {});
  }

  constructDefaultDescription({ listing = {}, listing_description = {} }) {
    return {
      title: listing.name,
      summary: listing_description.summary,
      space: listing_description.space,
      access: listing_description.access,
      interaction: listing_description.interaction,
      notes: listing_description.notes,
      neighborhood: listing_description.neighborhood_overview
    };
  }

  constructDefaultRoom({ listing = {}, listing_rooms = [] }) {
    return {
      numOfBathroom: listing.bathrooms,
      rooms: listing_rooms,
      propertyTypeGroup: listing.property_type_group,
      propertyTypeCategory: listing.property_type_category,
      roomTypeCategory: listing.room_type_category
    };
  }

  constructDefaultAmenities({ listing = {} }) {
    return {
      amenities: listing.amenity_categories.map(categoryKey => {
        return {
          key: categoryKey,
          value: splitUnderscoreAndCapitalize(categoryKey)
        };
      })
    };
  }

  constructDefaultAddress({ listing = {} }) {
    return {
      street: listing.street,
      city: listing.city,
      zipcode: listing.zipcode,
      state: listing.state,
      country: listing.country_code
    };
  }

  constructDefaultGuestResource({ listing = {} }) {
    return {
      checkinOption: (listing.check_in_option && listing.check_in_option.category) || '',
      checkinInstruction: (listing.check_in_option && listing.check_in_option.instruction) || ''
    };
  }

  constructDefaultCheckoutTasks({ listing = {} }, tasks) {
    const deepMerge = (a, b, fn) =>
      [...new Set([...Object.keys(a), ...Object.keys(b)])].reduce((acc, key) => ({ ...acc, [key]: fn(key, a[key], b[key]) }), {});
    const concatFn = (key, a, b) => {
      if (Array.isArray(a) && Array.isArray(b)) return a.concat(b);
      if (typeof a === 'object' && typeof b === 'object') return deepMerge(a, b, concatFn);
      //Since defaultCheckoutTask description is always empty string, then can just join('')
      if (typeof a === 'string' && typeof b === 'string') return [a, b].join('');
      return b ? b : a;
    };

    //   {
    //     "RETURN_KEYS": {
    //         "code": "return_keys",
    //         "label": "Return keys to a specific place/person on checking out.",
    //         "is_present": true,
    //         "task_detail": " Our check out time is 12pm,  may check out *before 12pm* and return the key access card back to Faaz Mart and *take return key photo to us*"
    //     },
    //     "TURN_THINGS_OFF": {
    //         "code": "turn_things_off",
    //         "label": "Turn lights/appliances etc off on leaving.",
    //         "is_present": true,
    //         "task_detail": " Hi can turn off the house electric switch ( except Washing Machine , Refrigerator) "
    //     },
    //     "THROW_TRASH": {
    //         "code": "throw_trash",
    //         "label": "Take care of trash before leaving.",
    //         "is_present": true,
    //         "task_detail": " Kindly throw the rubbish to Rubbish Can that we provided in unit, thank you~"
    //     },

    return listing.check_out_tasks ? deepMerge(tasks, listing.check_out_tasks, concatFn) : tasks;
  }

  constructDefaultPricing({ listing, pricing_setting = {} }) {
    const standardFeesKeyValue = pricing_setting.standard_fees
      ? pricing_setting.standard_fees.reduce((newStandardFees, fee) => {
          return {
            ...newStandardFees,
            [fee.fee_type]: fee
          };
        }, {})
      : {};
    const getFeeObj = key => {
      const feeObj = standardFeesKeyValue[key];
      if (feeObj) {
        return {
          ...feeObj,
          amount: calcCorrectFee(feeObj.amount_type, feeObj.amount, true)
        };
      } else {
        return defaultStandardFee;
      }
    };

    return {
      maxCapacity: listing.person_capacity,
      guestsIncluded: pricing_setting.guests_included,
      extraGuestFee: pricing_setting.price_per_extra_person,
      // cleaningFee: pricing_setting.cleaning_fee,
      weeklyDiscount: calcCorrectDiscountFactor(pricing_setting.weekly_price_factor, true),
      monthlyDiscount: calcCorrectDiscountFactor(pricing_setting.monthly_price_factor, true),
      // securityDeposit: pricing_setting.security_deposit,
      resortFee: getFeeObj(AIRBNB_RESORT_FEE_KEY),
      managementFee: getFeeObj(AIRBNB_MANAGEMENT_FEE_KEY),
      communityFee: getFeeObj(AIRBNB_COMMUNITY_FEE_KEY),
      linenFee: getFeeObj(AIRBNB_LINEN_FEE_KEY),
      stdCleaningFee: getFeeObj(AIRBNB_CLEANING_FEE),
      shortStayCleaningFee: getFeeObj(AIRBNB_SHORT_STAY_CLEANING_FEE),
      taxes: pricing_setting.pass_through_taxes
        ? pricing_setting.pass_through_taxes.map((items, i) => {
            return {
              taxType: items.tax_type,
              amountType: items.amount_type,
              taxAmount: items.amount,
              businessTaxID: items.business_tax_id,
              accommodationsTaxRegistrationNumber: items.tot_registration_id,
              minimumDaysTaxExemption: items.long_term_stay_exemption,
              taxable_base: items.taxable_base,
              key: i++
            };
          })
        : []
    };
  }

  constuctTaxDetails = pricing_setting => {
    return pricing_setting.map((items, i) => {
      return {
        taxType: items.tax_type,
        amountType: items.amount_type,
        taxAmount: items.amount,
        businessTaxID: items.business_tax_id,
        accommodationsTaxRegistrationNumber: items.tot_registration_id,
        minimumDaysTaxExemption: items.long_term_stay_exemption,
        taxable_base: items.taxable_base,
        key: i++
      };
    });
  };

  constructDefaultAvailability({ availability_rule = {} }) {
    const flatDays = (daysOfWeek = []) => daysOfWeek.map(day => day.day_of_week);

    const defaultDOWMinNightPayload = {
      minNightSun: availability_rule.default_min_nights,
      minNightMon: availability_rule.default_min_nights,
      minNightTue: availability_rule.default_min_nights,
      minNightWed: availability_rule.default_min_nights,
      minNightThu: availability_rule.default_min_nights,
      minNightFri: availability_rule.default_min_nights,
      minNightSat: availability_rule.default_min_nights
    };
    const dowMinNightPayload = guard(
      () =>
        availability_rule.day_of_week_min_nights.reduce((result, dowMinNight) => {
          const dayOfWeek = guard(() => dowMinNight.day_of_week);
          const minNightOfTheDay = guard(() => dowMinNight.min_nights);

          if (dayOfWeek === 0) {
            result.minNightSun = minNightOfTheDay;
          } else if (dayOfWeek === 1) {
            result.minNightMon = minNightOfTheDay;
          } else if (dayOfWeek === 2) {
            result.minNightTue = minNightOfTheDay;
          } else if (dayOfWeek === 3) {
            result.minNightWed = minNightOfTheDay;
          } else if (dayOfWeek === 4) {
            result.minNightThu = minNightOfTheDay;
          } else if (dayOfWeek === 5) {
            result.minNightFri = minNightOfTheDay;
          } else if (dayOfWeek === 6) {
            result.minNightSat = minNightOfTheDay;
          }

          return result;
        }, defaultDOWMinNightPayload),
      defaultDOWMinNightPayload
    );

    return {
      advanceNoticeInHours: (availability_rule.booking_lead_time && availability_rule.booking_lead_time.hours) || 0,
      preparationTime: (availability_rule.turnover_days && availability_rule.turnover_days.days) || 0,
      minNight: availability_rule.default_min_nights,
      ...dowMinNightPayload,
      enable_gap_nights: availability_rule.enable_gap_nights || false,
      gapNight: availability_rule.enable_gap_nights ? availability_rule.gap_nights || 1 : 1,
      maxNight: availability_rule.default_max_nights,
      checkInDaysAllowed: flatDays(availability_rule.day_of_week_check_in),
      checkOutDaysAllowed: flatDays(availability_rule.day_of_week_check_out)
    };
  }

  constructDefaultBookingSetting({ booking_setting = {} }) {
    const getCheckinTime = timeString => {
      if (!timeString || timeString === 'NOT_SELECTED') {
        return undefined;
      }
      return timeString;
    };
    const defaultToTrueIfNull = value => {
      if (value === null) {
        return true;
      }
      return value;
    };
    const getHouseRules = bookings => {
      if (bookings) {
        const house = {};
        bookings.map(booking => (house[booking.type] = booking.added_details));
        return house;
      }
      return bookings;
    };

    return {
      isSuitableForChildren: booking_setting.guest_controls && defaultToTrueIfNull(booking_setting.guest_controls.allows_children_as_host),
      isSuitableForInfant: booking_setting.guest_controls && defaultToTrueIfNull(booking_setting.guest_controls.allows_infants_as_host),
      isPartiesAllowed: booking_setting.guest_controls && booking_setting.guest_controls.allows_events_as_host,
      isPetsAllowed: booking_setting.guest_controls && booking_setting.guest_controls.allows_pets_as_host,
      isSmokingAllowed: booking_setting.guest_controls && booking_setting.guest_controls.allows_smoking_as_host,
      reasonWhyNotAllowChild: (booking_setting.guest_controls && booking_setting.guest_controls.children_not_allowed_details) || '',
      cancellationPolicy: booking_setting.cancellation_policy_settings && booking_setting.cancellation_policy_settings.cancellation_policy_category,
      checkInStartTime: getCheckinTime(booking_setting.check_in_time_start),
      checkInEndTime: getCheckinTime(booking_setting.check_in_time_end),
      checkOutTime: booking_setting.check_out_time,
      houseRules: (booking_setting.listing_expectations_for_guests && getHouseRules(booking_setting.listing_expectations_for_guests)) || [],
      nonRefundablePriceFactor: booking_setting.cancellation_policy_settings.non_refundable_price_factor || null
    };
  }

  constructRoomsOfRates = async ({ rateModifierTypeConstants, rateModifierSignConstants }) => {
    const { rate, roomType } = this.props;

    const webRates = await getWebRateByRoomType(roomType._id);
    const stdRate = webRates.data.find(rate => rate.code === STANDARD_RATE_CODE);

    let calculation = rate.calculation;

    if (!calculation) {
      const modifierTypeObject = rateModifierTypeConstants.find(type => type.isDefault);

      calculation = {
        amount: modifierTypeObject.isPercentage ? 0.01 : 1,
        type: modifierTypeObject.code,
        isPositive: !!rateModifierSignConstants.find(sign => sign.isDefault && sign.isPositive)
      };
    }

    calculation = {
      ...calculation,
      parent: {
        weekday: stdRate.weekday,
        weekend: stdRate.weekend
      }
    };

    const rateObj = {
      weekday: rate.weekday,
      weekend: rate.weekend,
      isDerived: rate.isDerived,
      calculation
    };

    const roomsOfRates = [
      {
        rates: [
          {
            rate: rateObj
          }
        ]
      }
    ];

    return roomsOfRates;
  };

  constuctDefaultMinExtraGuestFee = async (currency = 'MYR') => {
    const fxRate = await getCurrencyExchangeRate();
    if (fxRate.status !== 200) {
      return;
    }

    const USDRate = fxRate.data.find(rate => rate.code === 'USD');
    const selectedRate = fxRate.data.find(rate => rate.code === currency);
    const AirbnbMinExtraGuestFee = 5; // 5 USD
    var calculatedMinExtraGuestFee = 0;

    if (!selectedRate) {
      return;
    }
    switch (currency) {
      case 'USD':
        calculatedMinExtraGuestFee = AirbnbMinExtraGuestFee;
        break;

      case 'MYR':
        calculatedMinExtraGuestFee = Math.ceil(AirbnbMinExtraGuestFee * USDRate.selling);
        break;

      default:
        // Min rate (USD) => MYR => IDR
        calculatedMinExtraGuestFee = Math.ceil(AirbnbMinExtraGuestFee * USDRate.selling * (selectedRate.unit / selectedRate.selling));
        break;
    }

    this.setState({
      calculatedMinExtraGuestFee
    });
  };

  handleOnUpdate = (withValueConstraint = false, opportunityType, inputKey, event, constraintType = 'NONE') => {
    const { form } = this.props;
    const { opportunitiesToUpdate } = this.state;
    form.validateFields((err, values) => {
      let payloadBody = {
        type: opportunityType
      };
      if (!err) {
        if (constraintType === 'OPTION') {
          payloadBody.inputKey = inputKey;
          payloadBody.value = event.target.value;
          const index = opportunitiesToUpdate.findIndex(item => item.type === opportunityType);
          if (index > -1) {
            opportunitiesToUpdate[index] = payloadBody;
          } else {
            this.setState({
              opportunitiesToUpdate: [...opportunitiesToUpdate, payloadBody]
            });
          }
        } else if (event === true && constraintType === 'NONE') {
          this.setState({
            opportunitiesToUpdate: [...opportunitiesToUpdate, payloadBody]
          });
        } else if (event !== '' && (constraintType === 'INTEGER' || constraintType === 'PERCENTAGE')) {
          if (withValueConstraint) {
            if (inputKey === 'losSettings.maxNights') {
              payloadBody.inputKey = inputKey;
              payloadBody.value = event;
            } else {
              payloadBody.inputKey = inputKey;
              payloadBody.value = event;
            }
          }
          const index = opportunitiesToUpdate.findIndex(item => item.inputKey === inputKey);
          if (index === -1) {
            this.setState({
              opportunitiesToUpdate: [...opportunitiesToUpdate, payloadBody]
            });
          } else {
            opportunitiesToUpdate[index] = payloadBody;
          }
        } else {
          const index = opportunitiesToUpdate.findIndex(item => item.type === opportunityType);
          if (index > -1) {
            opportunitiesToUpdate.splice(index, 1);
          }
          this.setState({
            opportunitiesToUpdate: opportunitiesToUpdate
          });
        }
      }
    });
  };

  handleToggleEnable = (e, task) => {
    const { airbnbCheckoutTasks } = this.state;

    this.setState({
      airbnbCheckoutTasks: {
        ...airbnbCheckoutTasks,
        [task]: {
          ...airbnbCheckoutTasks[task],
          is_present: e
        }
      }
    });
  };

  handleGapNightToggle = e => {
    this.setState({
      toggleGapNight: e
    });
  };

  handleEnablePromotion = e => {
    this.setState({
      promotion: e
    });
  };

  handleEnableHighRatedGuestDiscount = e => {
    this.setState({
      highRatedGuestDiscount: e
    });
  };

  handleEnableMobileOnlyDiscount = e => {
    this.setState({
      mobileOnlyDiscount: e
    });
  };

  handleHighRatedGuestApplyToAllDates = applyToAllDates => {
    this.setState({
      highRatedGuestApplyToAllDates: applyToAllDates
    });
  };

  handleHighRatedGuestBlackoutDates = blackoutDates => {
    this.setState({
      highRatedGuestBlackoutDates: blackoutDates
    });
  };

  handleMobileOnlyApplyToAllDates = applyToAllDates => {
    this.setState({
      mobileOnlyApplyToAllDates: applyToAllDates
    });
  };

  handleMobileOnlyBlackoutDates = blackoutDates => {
    this.setState({
      mobileOnlyBlackoutDates: blackoutDates
    });
  };

  handleOnSave() {
    const { onConfirm, form, isFullSync } = this.props;
    const {
      opportunitiesToUpdate,
      airbnbCheckoutTasks,
      promotion,
      highRatedGuestDiscount,
      mobileOnlyDiscount,
      highRatedGuestApplyToAllDates,
      highRatedGuestBlackoutDates,
      mobileOnlyApplyToAllDates,
      mobileOnlyBlackoutDates,
      toggleGapNight
    } = this.state;
    const hasStairs = form.getFieldValue('hasStairs');
    const hasNoise = form.getFieldValue('hasNoise');
    const hasPets = form.getFieldValue('hasPets');
    const hasLimitedParking = form.getFieldValue('hasLimitedParking');
    const hasWeapons = form.getFieldValue('hasWeapons');
    const hasSharedSpaces = form.getFieldValue('hasSharedSpaces');
    const hasLimitedAmenities = form.getFieldValue('hasLimitedAmenities');
    const hasSurveillance = form.getFieldValue('hasSurveillance');
    const hasAnimals = form.getFieldValue('hasAnimals');
    const priceFactor = form.getFieldValue('priceFactor');
    const toggleNonRefundable = form.getFieldValue('toggleNonRefundable');

    form.validateFields((err, values) => {
      const constructStandardFees = formValues => {
        const standardFees = [];
        if (Number(formValues.resortFee) >= 0) {
          standardFees.push({
            fee_type: AIRBNB_RESORT_FEE_KEY,
            amount: calcCorrectFee(formValues.resortFeeType, formValues.resortFee),
            amount_type: formValues.resortFeeType
          });
        }

        if (Number(formValues.managementFee) >= 0) {
          standardFees.push({
            fee_type: AIRBNB_MANAGEMENT_FEE_KEY,
            amount: calcCorrectFee(formValues.managementFeeType, formValues.managementFee),
            amount_type: formValues.managementFeeType
          });
        }

        if (Number(formValues.communityFee) >= 0) {
          standardFees.push({
            fee_type: AIRBNB_COMMUNITY_FEE_KEY,
            amount: calcCorrectFee(formValues.communityFeeType, formValues.communityFee),
            amount_type: formValues.communityFeeType
          });
        }

        if (Number(formValues.linenFee) >= 0) {
          standardFees.push({
            fee_type: AIRBNB_LINEN_FEE_KEY,
            amount: calcCorrectFee(formValues.linenFeeType, formValues.linenFee),
            amount_type: formValues.linenFeeType
          });
        }

        if (Number(formValues.stdCleaningFee) >= 0) {
          standardFees.push({
            fee_type: AIRBNB_CLEANING_FEE,
            amount: calcCorrectFee(formValues.stdCleaningFeeType, formValues.stdCleaningFee),
            amount_type: formValues.stdCleaningFeeType
          });
        }

        if (Number(formValues.shortStayCleaningFee) > 0) {
          standardFees.push({
            fee_type: AIRBNB_SHORT_STAY_CLEANING_FEE,
            amount: calcCorrectFee(formValues.shortStayCleaningFeeType, formValues.shortStayCleaningFee),
            amount_type: formValues.shortStayCleaningFeeType
          });
        }
        return standardFees;
      };
      if (!err) {
        const highRatedGuestDatesToSave = highRatedGuestApplyToAllDates ? [] : highRatedGuestBlackoutDates;
        const mobileOnlyDatesToSave = mobileOnlyApplyToAllDates ? [] : mobileOnlyBlackoutDates;
        if (
          (highRatedGuestDiscount && getBlackoutQuotaExceededYear(highRatedGuestDatesToSave)) ||
          (mobileOnlyDiscount && getBlackoutQuotaExceededYear(mobileOnlyDatesToSave))
        ) {
          message.error(
            intl
              .get('hostConnect.integration.promotionTab.blackoutQuotaExceeded', {
                quota: AIRBNB_LISTING_PROMOTION_BLACKOUT_QUOTA
              })
              .d(`You can black out at most ${AIRBNB_LISTING_PROMOTION_BLACKOUT_QUOTA} days per calendar year for this discount.`)
          );
          return;
        }
        const fieldValues = form.getFieldsValue();
        const getCheckinTime = (startTime, endTime) => {
          if (startTime && endTime) {
            if (startTime.isAfter(endTime)) {
              return [endTime, startTime];
            } else {
              return [startTime, endTime];
            }
          }
          return [startTime, endTime];
        };
        const [checkInTimeStart, checkInTimeEnd] = getCheckinTime(values.checkInStartTime, values.checkInEndTime);
        const dayOfWeekMinNights = [
          { day_of_week: 0, min_nights: values.minNightSun },
          { day_of_week: 1, min_nights: values.minNightMon },
          { day_of_week: 2, min_nights: values.minNightTue },
          { day_of_week: 3, min_nights: values.minNightWed },
          { day_of_week: 4, min_nights: values.minNightThu },
          { day_of_week: 5, min_nights: values.minNightFri },
          { day_of_week: 6, min_nights: values.minNightSat }
        ];
        let taskObj = {};
        for (var key in airbnbCheckoutTasks) {
          if (airbnbCheckoutTasks[key].is_present === true) {
            taskObj = {
              ...taskObj,
              [key]: {
                is_present: true,
                task_detail: values[key].trim()
              }
            };
          } else {
            taskObj = {
              ...taskObj,
              [key]: {
                is_present: false
              }
            };
          }
        }

        const payload = {
          listing: {},
          listing_description: {},
          booking_setting: {},
          opportunities: opportunitiesToUpdate,
          pricing_setting: {
            guests_included: values.guestsIncluded,
            price_per_extra_person: values.extraGuestFee,
            // cleaning_fee: values.cleaningFee,
            weekly_price_factor: calcCorrectDiscountFactor(values.weeklyDiscount),
            monthly_price_factor: calcCorrectDiscountFactor(values.monthlyDiscount),
            // security_deposit: values.securityDeposit,
            standard_fees: constructStandardFees(values),
            pass_through_taxes:
              values.keys &&
              values.keys.map(key => {
                if (values.minimumDaysTaxExemption && values.minimumDaysTaxExemption[key]) {
                  return {
                    tax_type: values.taxType[key],
                    amount_type: values.amountType[key],
                    amount: values.taxAmount[key],
                    business_tax_id: values.businessTaxID[key],
                    tot_registration_id: values.accommodationsTaxRegistrationNumber[key],
                    attestation: true,
                    taxable_base: [
                      'base_price',
                      'pass_through_management_fee',
                      'pass_through_community_fee',
                      'pass_through_linen_fee',
                      'pass_through_resort_fee',
                      'pass_through_cleaning_fee',
                      'pass_through_pet_fee'
                    ],
                    long_term_stay_exemption: values.minimumDaysTaxExemption[key]
                  };
                } else {
                  return {
                    tax_type: values.taxType[key],
                    amount_type: values.amountType[key],
                    amount: values.taxAmount[key],
                    business_tax_id: values.businessTaxID[key],
                    tot_registration_id: values.accommodationsTaxRegistrationNumber[key],
                    attestation: true,
                    taxable_base: [
                      'base_price',
                      'pass_through_management_fee',
                      'pass_through_community_fee',
                      'pass_through_linen_fee',
                      'pass_through_resort_fee',
                      'pass_through_cleaning_fee',
                      'pass_through_pet_fee'
                    ]
                  };
                }
              })
          },
          availability_rule: {
            default_min_nights: values.minNight,
            day_of_week_min_nights: dayOfWeekMinNights,
            default_max_nights: values.maxNight,
            seasonal_min_nights: [
              {
                start_date: moment()
                  .subtract(1, 'day')
                  .format('YYYY-MM-DD'),
                end_date: moment().format('YYYY-MM-DD'),
                min_nights: values.minNight || 1
              }
            ],
            booking_lead_time:
              (values.advanceNotice && Number(values.advanceNotice) > 0) ||
              (values.customAdvanceNotice &&
                24 - Number(values.customAdvanceNotice.format('H')) > 0 &&
                24 - Number(values.customAdvanceNotice.format('H')) < 24)
                ? values.customAdvanceNotice && Number(values.customAdvanceNotice.format('H')) === 0
                  ? { allow_request_to_book: 0, hours: values.customAdvanceNotice && Number(values.customAdvanceNotice.format('H')) }
                  : {
                      allow_request_to_book: 1,
                      hours:
                        values.customAdvanceNotice &&
                        24 - Number(values.customAdvanceNotice.format('H')) > 0 &&
                        24 - Number(values.customAdvanceNotice.format('H')) < 24
                          ? 24 - Number(values.customAdvanceNotice.format('H'))
                          : values.advanceNotice
                    }
                : (values.advanceNotice && Number(values.advanceNotice) === 0) ||
                  (values.customAdvanceNotice && Number(values.customAdvanceNotice.format('H')) === 0)
                ? { allow_request_to_book: 0, hours: values.advanceNotice }
                : undefined,
            turnover_days: Number(values.preparationTime) >= 0 ? { days: values.preparationTime } : undefined,
            day_of_week_check_in: values.checkInDaysAllowed ? values.checkInDaysAllowed.map(day => ({ day_of_week: day })) : undefined,
            day_of_week_check_out: values.checkOutDaysAllowed ? values.checkOutDaysAllowed.map(day => ({ day_of_week: day })) : undefined
          },
          rateDistributionPayload: getRateDistributionPayload(fieldValues),
          promotion: promotion,
          listing_promotions: [
            {
              type: AIRBNB_LISTING_PROMOTION_TYPE.HIGH_RATED_GUEST_DISCOUNT,
              enabled: highRatedGuestDiscount,
              blackout_dates: highRatedGuestDatesToSave
            },
            {
              type: AIRBNB_LISTING_PROMOTION_TYPE.MOBILE_ONLY_DISCOUNT,
              enabled: mobileOnlyDiscount,
              blackout_dates: mobileOnlyDatesToSave
            }
          ],
          non_refundable_price_factor: toggleNonRefundable ? Number((1 - priceFactor / 100).toFixed(2)) : null,
          internalData: {
            gap_nights: values.gapNight,
            enable_gap_nights: toggleGapNight
          }
        };
        payload.listing.check_out_tasks = taskObj;
        if (isFullSync) {
          payload.listing = {
            name: values.title,
            property_type_group: values.propertyTypeGroup || undefined,
            property_type_category: values.propertyTypeCategory || undefined,
            room_type_category: values.roomTypeCategory || undefined,
            check_in_option: {
              category: values.checkinOption || undefined,
              instruction: values.checkinInstruction || undefined
            }
          };
          payload.booking_setting = {
            guest_controls: {
              allows_children_as_host: values.isSuitableForChildren,
              allows_infants_as_host: values.isSuitableForInfant,
              children_not_allowed_details: values.reasonWhyNotAllowChild,
              allows_pets_as_host: values.isPetsAllowed,
              allows_smoking_as_host: values.isSmokingAllowed,
              allows_events_as_host: values.isPartiesAllowed
            },
            check_in_time_start: checkInTimeStart ? checkInTimeStart.format('H') : undefined,
            check_in_time_end: checkInTimeEnd ? checkInTimeEnd.format('H') : undefined,
            check_out_time: values.checkOutTime ? values.checkOutTime.format('H') : undefined,
            cancellation_policy_category: values.cancellationPolicy,
            non_refundable_price_factor: toggleNonRefundable ? Number((1 - priceFactor / 100).toFixed(2)) : undefined,
            listing_expectations_for_guests: [
              hasStairs
                ? {
                    added_details: values.stairsDetails,
                    type: 'requires_stairs'
                  }
                : undefined,
              hasNoise
                ? {
                    added_details: values.noiseDetails,
                    type: 'potential_noise'
                  }
                : undefined,
              hasPets
                ? {
                    added_details: values.petsDetails,
                    type: 'has_pets'
                  }
                : undefined,
              hasLimitedParking
                ? {
                    added_details: values.limitedParkingDetails,
                    type: 'limited_parking'
                  }
                : undefined,
              hasSharedSpaces
                ? {
                    added_details: values.sharedSpacesDetails,
                    type: 'shared_spaces'
                  }
                : undefined,
              hasLimitedAmenities
                ? {
                    added_details: values.limitedAmenitiesDetails,
                    type: 'limited_amenities'
                  }
                : undefined,
              hasSurveillance
                ? {
                    added_details: values.surveillanceDetails,
                    type: 'surveillance'
                  }
                : undefined,
              hasWeapons
                ? {
                    added_details: values.weaponsDetails,
                    type: 'weapons'
                  }
                : undefined,
              hasAnimals
                ? {
                    added_details: values.animalsDetails,
                    type: 'animals'
                  }
                : undefined
            ].filter(k => k)
          };
        }

        onConfirm(payload);
      } else {
        let selectedTab = '';
        if (err.title || err.checkinInstruction) {
          selectedTab = 'details';
        } else if (err.extraGuestFee || err.weeklyDiscount || err.monthlyDiscount) {
          selectedTab = 'pricing';
        } else if (
          err.reasonWhyNotAllowChild ||
          err.stairsDetails ||
          err.noiseDetails ||
          err.petsDetails ||
          err.limitedParkingDetails ||
          err.sharedSpacesDetails ||
          err.limitedAmenitiesDetails ||
          err.surveillanceDetails ||
          err.weaponsDetails ||
          err.animalsDetails ||
          err.checkInEndTime
        ) {
          selectedTab = 'bookingSetting';
        } else if (
          Object.keys(err).includes('Rate Distribution Tab----weekdayRate') ||
          Object.keys(err).includes('Rate Distribution Tab----weekendRate') ||
          Object.keys(err).includes('Rate Distribution Tab----modifierAmount')
        ) {
          selectedTab = 'rateDistribution';
        } else {
          selectedTab = 'tax';
        }
        const { history } = this.props;
        let query = queryString.parse(history.location.search);
        query = {
          ...query,
          selectedTab
        };
        history.push(`${history.location.pathname}?${queryString.stringify(query)}`);
      }
    });
  }

  setQueryToLink = activeTab => {
    const { history } = this.props;
    let query = queryString.parse(history.location.search);
    query = {
      ...query,
      selectedTab: activeTab
    };
    history.push({
      search: `?${queryString.stringify(query)}`
    });
  };

  handleOnClose = e => {
    const { history, onClose } = this.props;
    let query = queryString.parse(history.location.search);
    delete query.selectedTab;
    history.push({
      search: `?${queryString.stringify(query)}`
    });
    onClose(e);
  };

  toggleCustomValue = e => {
    if (e.target.value === 0) {
      return this.setState({
        toggleCustomValue: true
      });
    } else {
      return this.setState({
        toggleCustomValue: false
      });
    }
  };

  render() {
    const { isVisible, form, isFullSync, location, currency } = this.props;
    const {
      isLoading,
      defaultDescription,
      defaultRoom,
      defaultAmenities,
      defaultAddress,
      defaultGuestResource,
      defaultPricing,
      defaultAvailability,
      defaultBookingSetting,
      propertyTypeGroups,
      propertyTypes,
      roomTypes,
      roomsOfRates,
      airbnbCheckinOptions,
      airbnbCheckoutTasks,
      toggleCustomValue,
      opportunities,
      calculatedMinExtraGuestFee,
      reservationIssues,
      promotion,
      highRatedGuestDiscount,
      highRatedGuestApplyToAllDates,
      highRatedGuestBlackoutDates,
      mobileOnlyDiscount,
      mobileOnlyApplyToAllDates,
      mobileOnlyBlackoutDates,
      airbnbPromotions,
      toggleGapNight
    } = this.state;
    const query = queryString.parse(location.search);
    const defaultActiveTabKey = isFullSync ? 'details' : 'pricing';
    return (
      <AdvancedOptionsModal isVisible={isVisible} isLoading={isLoading} onSave={this.handleOnSave} onClose={this.handleOnClose}>
        <Tabs
          defaultActiveKey={defaultActiveTabKey}
          onTabClick={this.setQueryToLink}
          activeKey={query.selectedTab ? query.selectedTab : defaultActiveTabKey}
          scrollable
        >
          {isFullSync && (
            <TabPane tab={intl.get('hostConnect.integration.headerLabels.detail').d('Details')} key="details">
              <DetailsForm
                form={form}
                defaultValues={{ ...defaultDescription, ...defaultRoom, ...defaultAmenities, ...defaultAddress, ...defaultGuestResource }}
                propertyTypeGroups={propertyTypeGroups}
                propertyTypes={propertyTypes}
                roomTypes={roomTypes}
                airbnbCheckinOptions={airbnbCheckinOptions}
              />
            </TabPane>
          )}
          {isFullSync && (
            <TabPane tab={intl.get('hostConnect.integration.headerLabels.bookingSetting').d('Booking Setting')} key="bookingSetting">
              <BookingSettingForm form={form} defaultValues={defaultBookingSetting} />
            </TabPane>
          )}
          <TabPane tab={intl.get('hostConnect.integration.headerLabels.pricing').d('Pricing')} key="pricing" forceRender>
            <PricingForm form={form} defaultValues={defaultPricing} currency={currency} minExtraGuestFee={calculatedMinExtraGuestFee} />
          </TabPane>
          <TabPane tab={intl.get('hostConnect.integration.headerLabels.availability').d('Availability')} key="availability" forceRender>
            <AvailabilityForm
              form={form}
              defaultValues={defaultAvailability}
              toggle={this.toggleCustomValue}
              toggleCustomValue={toggleCustomValue}
              toggleGapNight={toggleGapNight}
              gapNightToggle={this.handleGapNightToggle}
            />
          </TabPane>
          <TabPane tab={intl.get('hostConnect.integration.headerLabels.rateDistribution').d('Rate Distribution')} key="rateDistribution" forceRender>
            <AdvancedOptionsRateDistributionTab
              form={form}
              roomsOfRates={roomsOfRates}
              currency={currency}
              defaultBookingSetting={defaultBookingSetting}
              cancellationPolicyTab={false}
            />
          </TabPane>
          <TabPane
            tab={intl.get('hostConnect.integration.headerLabels.cancellationPolicy').d('Cancellation Policy')}
            key="cancellationPolicy"
            forceRender
          >
            <AdvancedOptionsRateDistributionTab
              form={form}
              roomsOfRates={roomsOfRates}
              currency={currency}
              defaultBookingSetting={defaultBookingSetting}
              cancellationPolicyTab={true}
            />
          </TabPane>
          {opportunities.length > 0 && (
            <TabPane tab={intl.get('hostConnect.integration.headerLabels.oppurtunity').d('Opportunities')} key="opportunities">
              <OpportunitiesTab form={form} defaultValues={opportunities} onUpdate={this.handleOnUpdate} />
            </TabPane>
          )}
          <TabPane tab={intl.get('hostConnect.integration.headerLabels.checkoutTasks').d('Check Out Tasks')} key="checkoutTasks" forceRender>
            <CheckoutTasksTab form={form} defaultValues={airbnbCheckoutTasks} onToggleEnable={this.handleToggleEnable} />
          </TabPane>
          {reservationIssues.length > 0 && (
            <TabPane tab={intl.get('hostConnect.integration.headerLabels.reservationIssues').d('Reservation Issues')} key="resIssues">
              <ResIssuesTab form={form} defaultValues={reservationIssues} />
            </TabPane>
          )}
          <TabPane tab={intl.get('hostConnect.integration.headerLabels.promotion').d('Promotion')} key="promotion" forceRender>
            <PromotionTab
              form={form}
              promotion={promotion}
              airbnbPromotions={airbnbPromotions}
              onChange={this.handleEnablePromotion}
              highRatedGuestDiscount={highRatedGuestDiscount}
              mobileOnlyDiscount={mobileOnlyDiscount}
              onChangeHighRatedGuestDiscount={this.handleEnableHighRatedGuestDiscount}
              onChangeMobileOnlyDiscount={this.handleEnableMobileOnlyDiscount}
              highRatedGuestApplyToAllDates={highRatedGuestApplyToAllDates}
              highRatedGuestBlackoutDates={highRatedGuestBlackoutDates}
              onChangeHighRatedGuestApplyToAllDates={this.handleHighRatedGuestApplyToAllDates}
              onChangeHighRatedGuestBlackoutDates={this.handleHighRatedGuestBlackoutDates}
              mobileOnlyApplyToAllDates={mobileOnlyApplyToAllDates}
              mobileOnlyBlackoutDates={mobileOnlyBlackoutDates}
              onChangeMobileOnlyApplyToAllDates={this.handleMobileOnlyApplyToAllDates}
              onChangeMobileOnlyBlackoutDates={this.handleMobileOnlyBlackoutDates}
            />
          </TabPane>
          <TabPane tab={intl.get('hostConnect.integration.headerLabels.tax').d('Tax')} key="tax" forceRender>
            <TaxForm form={form} defaultValues={defaultPricing.taxes} currency={currency} />
          </TabPane>
        </Tabs>
      </AdvancedOptionsModal>
    );
  }
}

AirbnbAdvanceOptionModal.propTypes = {
  airbnbListingId: PropTypes.string,
  defaultValues: PropTypes.object.isRequired,
  isFullSync: PropTypes.bool.isRequired,
  isVisible: PropTypes.bool.isRequired,
  rateModifierSignConstants: PropTypes.array.isRequired,
  rateModifierTypeConstants: PropTypes.array.isRequired,
  unitId: PropTypes.string.isRequired,
  onClose: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
  currency: PropTypes.string.isRequired
};

AirbnbAdvanceOptionModal.defaultProps = {
  isVisible: false,
  currency: 'RM',
  onConfirm: () => {},
  onClose: () => {}
};

export default withRouter(Form.create()(AirbnbAdvanceOptionModal));
